import {
    buildFlowActionSelectionState as buildFlowActionSelectionStateHelper,
    buildFlowMotionEditorState as buildFlowMotionEditorStateHelper,
    buildFlowMotionAppliedDataState as buildFlowMotionAppliedDataStateHelper,
    mountFlowMotionEditor as mountFlowMotionEditorHelper,
    mountFlowConditionEditor as mountFlowConditionEditorHelper,
    buildFlowConditionSavedState as buildFlowConditionSavedStateHelper,
    mountFlowSignalOutputEditor as mountFlowSignalOutputEditorHelper,
    buildFlowSignalOutputAppliedDataState as buildFlowSignalOutputAppliedDataStateHelper,
    mountFlowWaitEditor as mountFlowWaitEditorHelper,
    buildFlowWaitSyncState as buildFlowWaitSyncStateHelper,
    mountFlowSubprocessEditor as mountFlowSubprocessEditorHelper,
    buildFlowSubprocessSyncState as buildFlowSubprocessSyncStateHelper,
    mountFlowErrorEditor as mountFlowErrorEditorHelper,
    buildFlowErrorSyncState as buildFlowErrorSyncStateHelper,
    mountFlowButtonEditor as mountFlowButtonEditorHelper,
    buildFlowButtonSyncState as buildFlowButtonSyncStateHelper,
    mountFlowEndEditor as mountFlowEndEditorHelper,
    buildFlowEndSyncState as buildFlowEndSyncStateHelper,
    normalizePneumaticValveMode
} from "../frontend/control-spec-helper.js";

export function setActiveFlowModule(deps = {}) {
    const {
        moduleName = "Home",
        state,
        ensureDrawflowModule = () => {},
        renderFlowTabs = () => {},
        renderFlowNodeEditor = () => {},
        renderFlowConnectionAssist = () => {},
        bindConditionNodeSelection = () => {},
        scheduleFlowConnectionRefresh = () => {},
        drawflowRoot = null,
        doc = document
    } = deps;
    const target = String(moduleName || "Home");
    if (!state) return target;
    if (!state.drawflow) {
        state.activeFlowModuleName = target;
        return target;
    }
    ensureDrawflowModule(target);
    if (typeof state.drawflow.changeModule === "function") {
        try { state.drawflow.changeModule(target); } catch (error) { console.warn("changeModule failed", error); }
    }
    state.activeFlowModuleName = target;
    state.selectedFlowNodeId = null;
    state.pendingFlowConnection = null;
    renderFlowTabs();
    renderFlowNodeEditor();
    renderFlowConnectionAssist();
    bindConditionNodeSelection(drawflowRoot || doc.getElementById("drawflow-container") || doc);
    scheduleFlowConnectionRefresh();
    return target;
}

export function getActiveFlowModuleName(deps = {}) {
    const { state } = deps;
    return state?.activeFlowModuleName || "Home";
}

export function getDrawflowAdapter(deps = {}) {
    const {
        state,
        win = window,
        createDrawflowAdapter = win?.__createDrawflowAdapter
    } = deps;
    if (!win.__drawflowAdapter && typeof createDrawflowAdapter === "function") {
        win.__drawflowAdapter = createDrawflowAdapter({
            getDrawflow: () => state?.drawflow,
            getActiveModuleName: () => state?.activeFlowModuleName || "Home",
            getFallbackData: () => state?.project?.flow?.drawflowData || {}
        });
    }
    return win.__drawflowAdapter;
}

export function getFlowExportSnapshot(deps = {}) {
    const { getDrawflowAdapter = () => ({ getExportSnapshot: () => ({}) }) } = deps;
    return getDrawflowAdapter().getExportSnapshot();
}

export function getFlowModuleData(deps = {}) {
    const {
        snapshot,
        moduleName,
        getFlowExportSnapshot = () => ({}),
        getActiveFlowModuleName = () => "Home",
        getDrawflowAdapter = () => ({ getModuleData: () => ({}) })
    } = deps;
    return getDrawflowAdapter().getModuleData(
        snapshot ?? getFlowExportSnapshot(),
        moduleName ?? getActiveFlowModuleName()
    );
}

export function getMainFlowDataSnapshot(deps = {}) {
    const {
        getFlowExportSnapshot = () => ({}),
        getFlowModuleData = () => ({})
    } = deps;
    return getFlowModuleData(getFlowExportSnapshot(), "Home");
}

export function getActiveFlowDataSnapshot(deps = {}) {
    const { getDrawflowAdapter = () => ({ getActiveModuleData: () => ({}) }) } = deps;
    return getDrawflowAdapter().getActiveModuleData();
}

export function hasFlowModuleNodes(deps = {}) {
    const {
        moduleName,
        snapshot = {},
        getFlowModuleData = (source, name) => source?.drawflow?.[name]?.data || {}
    } = deps;
    if (!moduleName) return false;
    return Object.keys(getFlowModuleData(snapshot, moduleName) || {}).length > 0;
}

export function refreshAllFlowConnections(deps = {}) {
    const { getDrawflowAdapter = () => ({ refreshConnections: () => false }) } = deps;
    return getDrawflowAdapter().refreshConnections();
}

export function scheduleFlowConnectionRefresh(deps = {}) {
    const { getDrawflowAdapter = () => ({ scheduleConnectionRefresh: () => false }) } = deps;
    return getDrawflowAdapter().scheduleConnectionRefresh();
}

export function getFlowNodeById(deps = {}) {
    const {
        state,
        nodeId,
        getActiveFlowModuleName = () => "Home"
    } = deps;
    if (!nodeId || !state?.drawflow) return null;
    return state.drawflow?.drawflow?.drawflow?.[getActiveFlowModuleName()]?.data?.[nodeId] || null;
}

export function getFlowNodeLabel(deps = {}) {
    const {
        state,
        nodeId,
        getActiveFlowModuleName = () => "Home",
        getNodeData = (node) => node?.data || {},
        resolveFlowNodeLabelFromData = (data, node) => node?.name || data?.label || "Node"
    } = deps;
    const node = getFlowNodeById({
        state,
        nodeId,
        getActiveFlowModuleName
    });
    const data = getNodeData(node);
    return resolveFlowNodeLabelFromData(data, node);
}

export function getFlowNodeIdFromElement(deps = {}) {
    const { target } = deps;
    const nodeEl = target?.closest?.("[id^='node-']");
    if (!nodeEl) return "";
    return String(nodeEl.id || "").replace(/^node-/, "");
}

export function getFlowPortName(deps = {}) {
    const {
        target,
        prefix = ""
    } = deps;
    if (!target?.classList) return "";
    for (const className of target.classList) {
        if (className.startsWith(prefix)) return className;
    }
    return "";
}

export function initializeDrawflowSurface(deps = {}) {
    const {
        state,
        container,
        DrawflowClass,
        ensureProjectFlowState = () => ({}),
        renderFlowTabs = () => {}
    } = deps;
    if (!state || !container || typeof DrawflowClass !== "function") return null;
    state.drawflow = new DrawflowClass(container);
    state.drawflow.reroute = false;
    state.drawflow.start();
    state.activeFlowModuleName = "Home";
    ensureProjectFlowState(state.project);
    renderFlowTabs();
    return state.drawflow;
}

export function refreshHomeMotionNodeMaps(deps = {}) {
    const {
        state,
        snapshot = {},
        getFlowModuleData = () => ({})
    } = deps;
    if (!state) return false;
    state.nodeIdToMotionId = {};
    state.motionIdToNodeId = {};
    const homeData = getFlowModuleData(snapshot, "Home");
    for (const nodeId of Object.keys(homeData || {})) {
        const motionId = String(homeData[nodeId]?.data?.motionId || "").trim();
        if (!motionId) continue;
        state.nodeIdToMotionId[String(nodeId)] = motionId;
        state.motionIdToNodeId[motionId] = String(nodeId);
    }
    return true;
}

export function hasSubprocessReferenceNode(deps = {}) {
    const {
        subprocessId,
        exported = {},
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || ""
    } = deps;
    if (!subprocessId) return false;
    for (const moduleData of Object.values(exported?.drawflow || {})) {
        for (const node of Object.values(moduleData?.data || {})) {
            const data = getNodeData(node);
            if (getNodeTypeFromData(data) === "subprocess" && String(data.subprocessId || "") === String(subprocessId)) {
                return true;
            }
        }
    }
    return false;
}

export function ensureDrawflowModule(deps = {}) {
    const {
        state,
        moduleName,
        getFlowExportSnapshot = () => ({})
    } = deps;
    if (!state?.drawflow || !moduleName || moduleName === "Home") return false;
    const snapshot = getFlowExportSnapshot();
    if (snapshot?.drawflow?.[moduleName]) return true;
    if (typeof state.drawflow.addModule === "function") {
        try { state.drawflow.addModule(moduleName); } catch (_error) {}
    }
    return true;
}

export function clearCustomDrawflowModules(deps = {}) {
    const {
        state,
        getFlowExportSnapshot = () => ({ drawflow: {} })
    } = deps;
    if (!state?.drawflow || typeof state.drawflow.removeModule !== "function") return false;
    const snapshot = getFlowExportSnapshot();
    for (const moduleName of Object.keys(snapshot.drawflow || {})) {
        if (moduleName === "Home") continue;
        try { state.drawflow.removeModule(moduleName); } catch (_error) {}
    }
    return true;
}

export function removeSubprocessMeta(deps = {}) {
    const {
        state,
        subprocessId,
        ensureProjectFlowState = () => ({}),
        findSubprocessById = () => null,
        getSubprocessList = () => [],
        getSubprocessModuleName = (id) => id,
        setActiveFlowModule = () => {},
        renderFlowTabs = () => {}
    } = deps;
    const flow = ensureProjectFlowState(state?.project);
    const subprocess = findSubprocessById(subprocessId, state?.project);
    if (!subprocess) return false;
    flow.subprocesses = getSubprocessList(state?.project).filter((item) => item.id !== subprocessId);
    const moduleName = subprocess.moduleName || getSubprocessModuleName(subprocess.id);
    if (state?.activeFlowModuleName === moduleName) {
        setActiveFlowModule("Home");
    }
    if (state?.drawflow && typeof state.drawflow.removeModule === "function") {
        try { state.drawflow.removeModule(moduleName); } catch (_error) {}
    }
    renderFlowTabs();
    return true;
}

export function countIncomingConnections(deps = {}) {
    const { node } = deps;
    if (!node?.inputs) return 0;
    let count = 0;
    for (const inputName in node.inputs) {
        count += node.inputs[inputName].connections.length;
    }
    return count;
}

export function getNodeIncomingConnections(deps = {}) {
    const { node } = deps;
    const connections = [];
    if (!node?.inputs) return connections;
    for (const inputName in node.inputs) {
        for (const conn of node.inputs[inputName].connections) {
            connections.push({ ...conn, targetInput: inputName });
        }
    }
    return connections;
}

export function removeFlowConnectionsToMotion(deps = {}) {
    const {
        state,
        motionId,
        exceptSourceNodeId,
        getFlowModuleData = () => ({})
    } = deps;
    if (!state?.drawflow) return false;
    const targetNodeId = state.motionIdToNodeId?.[motionId];
    if (!targetNodeId) return false;
    const exported = state.drawflow.export?.() || {};
    const homeData = getFlowModuleData(exported, "Home");
    const targetNode = homeData?.[targetNodeId];
    if (!targetNode?.inputs) return false;
    state.suspendFlowEvents = true;
    try {
        for (const inputName in targetNode.inputs) {
            const connections = targetNode.inputs[inputName]?.connections?.slice?.() || [];
            for (const connection of connections) {
                if (exceptSourceNodeId && String(connection.node) === String(exceptSourceNodeId)) continue;
                try {
                    state.drawflow.removeSingleConnection(String(connection.node), String(targetNodeId), connection.input, inputName);
                } catch (_error) {}
            }
        }
    } finally {
        state.suspendFlowEvents = false;
    }
    return true;
}

export function addFlowConnection(deps = {}) {
    const {
        state,
        sourceMotionId,
        targetMotionId
    } = deps;
    if (!state?.drawflow) return false;
    const sourceNodeId = state.motionIdToNodeId?.[sourceMotionId];
    const targetNodeId = state.motionIdToNodeId?.[targetMotionId];
    if (!sourceNodeId || !targetNodeId) return false;
    state.suspendFlowEvents = true;
    try {
        try {
            state.drawflow.addConnection(sourceNodeId, targetNodeId, "output_1", "input_1");
        } catch (_error) {}
    } finally {
        state.suspendFlowEvents = false;
    }
    return true;
}

export function updateAllFlowNodeLabels(deps = {}) {
    const {
        state,
        onlyAxisId,
        doc = document,
        getFlowModuleData = () => ({}),
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || "",
        findMotion = () => null,
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {}
    } = deps;
    if (!state?.drawflow) return false;
    state.suspendFlowEvents = true;
    try {
        const exported = state.drawflow.export?.() || {};
        for (const moduleName of Object.keys(exported.drawflow || {})) {
            const moduleData = getFlowModuleData(exported, moduleName);
            for (const nodeId in moduleData) {
                const node = moduleData[nodeId];
                const data = getNodeData(node);
                const nodeType = getNodeTypeFromData(data);
                const elem = doc.getElementById(`node-${nodeId}`);
                if (!elem) continue;
                const inner = elem.querySelector(".drawflow_content_node");
                if (!inner) continue;
                if (nodeType === "motion") {
                    const found = data.motionId ? findMotion(data.motionId) : null;
                    if (!found) continue;
                    if (onlyAxisId && found.axis.id !== onlyAxisId) continue;
                    inner.innerHTML = buildFlowNodeHtml(data, found.axis, found.motion);
                } else if (!onlyAxisId) {
                    inner.innerHTML = buildFlowNodeHtml(data);
                }
                bindConditionNodeSelection(inner);
            }
        }
    } finally {
        state.suspendFlowEvents = false;
    }
    return true;
}

export function formatConditionBranchText(deps = {}) {
    const { data = {}, outputName = "output_1" } = deps;
    const conditionText = (data.conditionText || "条件").trim();
    let branchText;
    if (outputName === "output_2") branchText = data.falseLabel || "OFF";
    else if (outputName === "output_3") branchText = `${data.timeoutLabel || "TIMEOUT"} ${data.timeoutSec ?? 0}s`;
    else branchText = data.trueLabel || "ON";
    branchText = String(branchText).trim();
    return conditionText ? `${conditionText} [${branchText}]` : branchText;
}

export function resolveFlowConnection(deps = {}) {
    const {
        state,
        homeData = {},
        connection = {},
        visited = new Set(),
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || "",
        getSubprocessNodeDuration = () => 0,
        getWaitNodeDuration = () => 0,
        getNodeIncomingConnections = () => [],
        formatConditionBranchText = ({ data = {}, outputName = "output_1" }) => {
            const conditionText = (data.conditionText || "条件").trim();
            let branchText;
            if (outputName === "output_2") branchText = data.falseLabel || "OFF";
            else if (outputName === "output_3") branchText = `${data.timeoutLabel || "TIMEOUT"} ${data.timeoutSec ?? 0}s`;
            else branchText = data.trueLabel || "ON";
            branchText = String(branchText).trim();
            return conditionText ? `${conditionText} [${branchText}]` : branchText;
        }
    } = deps;
    const sourceNodeId = String(connection.node);
    const sourceNode = homeData[sourceNodeId];
    const data = getNodeData(sourceNode);
    const nodeType = getNodeTypeFromData(data);
    const outputName = connection.input || connection.output || "output_1";

    if (visited.has(sourceNodeId)) {
        return { sourceMotionId: "", absoluteStart: true, absoluteStartSec: 0, conditionParts: [], warning: "循環参照" };
    }
    const nextVisited = new Set(visited);
    nextVisited.add(sourceNodeId);

    if (nodeType === "motion") {
        return { sourceMotionId: data.motionId || state?.nodeIdToMotionId?.[sourceNodeId] || "", absoluteStart: false, absoluteStartSec: null, conditionParts: [] };
    }
    if (nodeType === "subprocess") {
        const times = state?.computed?.subprocessTimes?.[sourceNodeId];
        return {
            sourceMotionId: "",
            absoluteStart: false,
            absoluteStartSec: times?.endSec ?? getSubprocessNodeDuration(data),
            conditionParts: []
        };
    }
    if (nodeType === "wait") {
        const times = state?.computed?.subprocessTimes?.[sourceNodeId];
        return {
            sourceMotionId: "",
            absoluteStart: false,
            absoluteStartSec: times?.endSec ?? getWaitNodeDuration(data),
            conditionParts: []
        };
    }
    if (nodeType === "start") {
        return { sourceMotionId: "", absoluteStart: true, absoluteStartSec: 0, conditionParts: [(data.label || "開始").trim()].filter(Boolean) };
    }
    if (nodeType === "condition") {
        const incoming = getNodeIncomingConnections(sourceNode);
        let base = { sourceMotionId: "", absoluteStart: true, absoluteStartSec: 0, conditionParts: [] };
        if (incoming.length > 1) {
            base.warning = `条件ノード「${data.conditionText || "条件"}」への複数入力は未対応です`;
        }
        if (incoming.length > 0) {
            base = resolveFlowConnection({
                state,
                homeData,
                connection: incoming[0],
                visited: nextVisited,
                getNodeData,
                getNodeTypeFromData,
                getSubprocessNodeDuration,
                getWaitNodeDuration,
                getNodeIncomingConnections,
                formatConditionBranchText
            });
        }
        return {
            sourceMotionId: base.sourceMotionId || "",
            absoluteStart: base.absoluteStart,
            absoluteStartSec: base.absoluteStartSec,
            warning: base.warning,
            conditionParts: [...(base.conditionParts || []), formatConditionBranchText({ data, outputName })].filter(Boolean)
        };
    }
    return { sourceMotionId: "", absoluteStart: true, absoluteStartSec: 0, conditionParts: [] };
}

export function initializeDrawflowController(deps = {}) {
    const {
        state,
        container,
        syncFlowEditorHeight = () => {},
        initializeDrawflowSurface = () => null,
        DrawflowClass,
        ensureProjectFlowState = () => ({}),
        renderFlowTabs = () => {},
        bindDrawflowLifecycleEvents = () => false,
        handleFlowConnectionCreated = () => false,
        handleFlowConnectionRemoved = () => false,
        handleFlowNodeRemoved = () => false,
        getActiveFlowModuleName = () => "Home",
        getFlowExportSnapshot = () => ({}),
        getFlowModuleData = () => ({}),
        findMotion = () => null,
        countIncomingConnections = () => 0,
        removeFlowConnectionsToMotion = () => {},
        rebuildStateFromFlow = () => {},
        scheduleAutoSave = () => {},
        alertMessage = () => {},
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || "",
        pushTransientWarning = () => {},
        hasSubprocessReferenceNode = () => false,
        removeSubprocessMeta = () => {},
        initializeFlowEditorUi = () => false,
        bindConditionNodeSelection = () => {},
        renderFlowNodeEditor = () => {},
        renderFlowConnectionAssist = () => {},
        bindFlowEditorInteractionEvents = () => false,
        doc = null,
        viewport = null,
        handleFlowCanvasDoubleClick = () => false,
        getActiveFlowDataSnapshot = () => ({}),
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        bindFlowCanvasPointerEvents = () => false,
        handleFlowConnectionPickMouseDown = () => false,
        handleFlowConnectionPickClick = () => false,
        handleFlowCanvasClick = () => false,
        getFlowNodeIdFromElement = () => "",
        getFlowPortName = () => "",
        beginFlowConnection = () => false,
        completeFlowConnection = () => false,
        selectFlowNodeByElement = () => false,
        closeFlowNodeEditor = () => false,
        bindFlowEditorViewportEvents = () => false,
        scheduleFlowConnectionRefresh = () => {},
        positionFlowNodeEditor = () => false,
        bindFlowEditorKeydownEvent = () => false,
        handleFlowEditorKeydown = () => false,
        clearPendingFlowConnection = () => false
    } = deps;
    syncFlowEditorHeight();
    initializeDrawflowSurface({
        state,
        container,
        DrawflowClass,
        ensureProjectFlowState,
        renderFlowTabs
    });
    bindDrawflowLifecycleEvents({
        drawflow: state?.drawflow,
        state,
        handleFlowConnectionCreated,
        handleFlowConnectionRemoved,
        handleFlowNodeRemoved,
        getActiveFlowModuleName,
        getFlowExportSnapshot,
        getFlowModuleData,
        findMotion,
        countIncomingConnections,
        removeFlowConnectionsToMotion,
        rebuildStateFromFlow,
        scheduleAutoSave,
        alertMessage,
        getNodeData,
        getNodeTypeFromData,
        pushTransientWarning,
        hasSubprocessReferenceNode,
        removeSubprocessMeta
    });
    initializeFlowEditorUi({
        root: container,
        bindConditionNodeSelection,
        renderFlowNodeEditor,
        renderFlowConnectionAssist
    });
    bindFlowEditorInteractionEvents({
        container,
        doc,
        viewport,
        state,
        handleFlowCanvasDoubleClick,
        renderFlowNodeEditor,
        getActiveFlowDataSnapshot,
        getNodeData,
        getNodeTypeFromData,
        findMotion,
        renderTimingGrid,
        renderSpeedChart,
        bindFlowCanvasPointerEvents,
        handleFlowConnectionPickMouseDown,
        handleFlowConnectionPickClick,
        handleFlowCanvasClick,
        getFlowNodeIdFromElement,
        getFlowPortName,
        beginFlowConnection,
        completeFlowConnection,
        selectFlowNodeByElement,
        closeFlowNodeEditor,
        bindFlowEditorViewportEvents,
        scheduleFlowConnectionRefresh,
        syncFlowEditorHeight,
        positionFlowNodeEditor,
        bindFlowEditorKeydownEvent,
        handleFlowEditorKeydown,
        clearPendingFlowConnection
    });
    return true;
}

export function initDrawflow(deps = {}) {
    const {
        doc = document,
        initializeDrawflowController = () => false
    } = deps;
    const container = doc?.getElementById?.("drawflow-container") || null;
    return initializeDrawflowController({
        ...deps,
        container
    });
}

export function addFlowMotionNode(deps = {}) {
    const {
        state,
        axis,
        motion,
        prevNodeId = null,
        options = {},
        getActiveFlowDataSnapshot = () => ({}),
        buildMotionNodeData = ({ axis, motion }) => ({ axisId: axis?.id, motionId: motion?.id }),
        buildFlowNodeHtml = () => "",
        getActiveFlowModuleName = () => "Home",
        bindConditionNodeSelection = () => {},
        doc = null
    } = deps;
    if (!state?.drawflow || !axis || !motion) return null;
    state.suspendFlowEvents = true;
    let id = null;
    try {
        const count = Object.keys(getActiveFlowDataSnapshot()).length;
        const x = options.x ?? (50 + count * 60 % 600);
        const y = options.y ?? (50 + Math.floor(count / 10) * 120);
        const nodeData = {
            axisId: axis.id,
            motionId: motion.id,
            ...(buildMotionNodeData({ axis, motion }) || {})
        };
        id = state.drawflow.addNode(
            "motion",
            1, 1,
            x, y,
            "motion",
            nodeData,
            buildFlowNodeHtml({ ...nodeData, nodeType: "motion" }, axis, motion)
        );
        if (getActiveFlowModuleName() === "Home") {
            state.nodeIdToMotionId[String(id)] = motion.id;
            state.motionIdToNodeId[motion.id] = String(id);
        }
        if (prevNodeId) {
            state.drawflow.addConnection(prevNodeId, String(id), "output_1", "input_1");
        }
        const nodeRoot = doc?.getElementById?.("node-" + id) || doc;
        bindConditionNodeSelection(nodeRoot);
    } finally {
        state.suspendFlowEvents = false;
    }
    return String(id);
}

export function addStartFlowNode(deps = {}) {
    const {
        state,
        options = {},
        getActiveFlowDataSnapshot = () => ({}),
        makeStartNodeData = () => ({ label: "Start" }),
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        doc = null,
        rebuildAll = () => {}
    } = deps;
    if (!state?.drawflow) return null;
    state.suspendFlowEvents = true;
    let id = null;
    try {
        const count = Object.keys(getActiveFlowDataSnapshot()).length;
        const data = Object.assign(makeStartNodeData(), options.data || {});
        id = state.drawflow.addNode(
            "start",
            0, 1,
            options.x ?? (60 + (count % 5) * 170), options.y ?? (30 + Math.floor(count / 5) * 130),
            "start",
            data,
            buildFlowNodeHtml(data)
        );
        const drawflowRoot = doc?.getElementById?.("drawflow-container") || doc;
        bindConditionNodeSelection(drawflowRoot);
    } finally {
        state.suspendFlowEvents = false;
    }
    rebuildAll();
    return String(id);
}

export function addConditionFlowNode(deps = {}) {
    const {
        state,
        options = {},
        getActiveFlowDataSnapshot = () => ({}),
        makeConditionNodeData = () => ({}),
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        doc = null,
        rebuildAll = () => {}
    } = deps;
    if (!state?.drawflow) return null;
    const data = Object.assign(makeConditionNodeData(), options.data || {});
    state.suspendFlowEvents = true;
    let id = null;
    try {
        const count = Object.keys(getActiveFlowDataSnapshot()).length;
        id = state.drawflow.addNode(
            "condition",
            1, 3,
            options.x ?? (180 + (count % 5) * 170), options.y ?? (30 + Math.floor(count / 5) * 130),
            "condition",
            data,
            buildFlowNodeHtml(data)
        );
        const drawflowRoot = doc?.getElementById?.("drawflow-container") || doc;
        bindConditionNodeSelection(drawflowRoot);
    } finally {
        state.suspendFlowEvents = false;
    }
    rebuildAll();
    return String(id);
}

export function addButtonFlowNode(deps = {}) {
    const {
        state,
        options = {},
        getActiveFlowDataSnapshot = () => ({}),
        makeButtonNodeData = () => ({}),
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        doc = null,
        rebuildAll = () => {}
    } = deps;
    if (!state?.drawflow) return null;
    const data = Object.assign(makeButtonNodeData(), options.data || {});
    state.suspendFlowEvents = true;
    let id = null;
    try {
        const count = Object.keys(getActiveFlowDataSnapshot()).length;
        id = state.drawflow.addNode(
            "button",
            1, 2,
            options.x ?? (180 + (count % 5) * 170), options.y ?? (30 + Math.floor(count / 5) * 130),
            "button",
            data,
            buildFlowNodeHtml(data)
        );
        const drawflowRoot = doc?.getElementById?.("drawflow-container") || doc;
        bindConditionNodeSelection(drawflowRoot);
    } finally {
        state.suspendFlowEvents = false;
    }
    rebuildAll();
    return String(id);
}

export function addErrorFlowNode(deps = {}) {
    const {
        state,
        options = {},
        getActiveFlowDataSnapshot = () => ({}),
        makeErrorNodeData = () => ({}),
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        doc = null,
        rebuildAll = () => {}
    } = deps;
    if (!state?.drawflow) return null;
    const data = Object.assign(makeErrorNodeData(), options.data || {});
    state.suspendFlowEvents = true;
    let id = null;
    try {
        const count = Object.keys(getActiveFlowDataSnapshot()).length;
        id = state.drawflow.addNode(
            "error",
            1, 0,
            options.x ?? (420 + (count % 5) * 170), options.y ?? (60 + Math.floor(count / 5) * 130),
            "error",
            data,
            buildFlowNodeHtml(data)
        );
        const drawflowRoot = doc?.getElementById?.("drawflow-container") || doc;
        bindConditionNodeSelection(drawflowRoot);
    } finally {
        state.suspendFlowEvents = false;
    }
    rebuildAll();
    return String(id);
}

export function addSignalOutputFlowNode(deps = {}) {
    const {
        state,
        options = {},
        getActiveFlowDataSnapshot = () => ({}),
        makeSignalOutputNodeData = () => ({}),
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        doc = null,
        rebuildAll = () => {}
    } = deps;
    if (!state?.drawflow) return null;
    const data = Object.assign(makeSignalOutputNodeData(), options.data || {});
    state.suspendFlowEvents = true;
    let id = null;
    try {
        const count = Object.keys(getActiveFlowDataSnapshot()).length;
        id = state.drawflow.addNode(
            "signal_output",
            1, 1,
            options.x ?? (520 + (count % 5) * 170), options.y ?? (90 + Math.floor(count / 5) * 130),
            "signal-output",
            data,
            buildFlowNodeHtml(data)
        );
        const drawflowRoot = doc?.getElementById?.("drawflow-container") || doc;
        bindConditionNodeSelection(drawflowRoot);
    } finally {
        state.suspendFlowEvents = false;
    }
    rebuildAll();
    return String(id);
}

export function addWaitFlowNode(deps = {}) {
    const {
        state,
        options = {},
        getActiveFlowDataSnapshot = () => ({}),
        makeWaitNodeData = () => ({}),
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        doc = null,
        rebuildAll = () => {}
    } = deps;
    if (!state?.drawflow) return null;
    const data = Object.assign(makeWaitNodeData(), options.data || {});
    state.suspendFlowEvents = true;
    let id = null;
    try {
        const count = Object.keys(getActiveFlowDataSnapshot()).length;
        id = state.drawflow.addNode(
            "wait",
            1, 1,
            options.x ?? (600 + (count % 5) * 170), options.y ?? (120 + Math.floor(count / 5) * 130),
            "wait",
            data,
            buildFlowNodeHtml(data)
        );
        const drawflowRoot = doc?.getElementById?.("drawflow-container") || doc;
        bindConditionNodeSelection(drawflowRoot);
    } finally {
        state.suspendFlowEvents = false;
    }
    rebuildAll();
    return String(id);
}

export function addEndFlowNode(deps = {}) {
    const {
        state,
        options = {},
        getActiveFlowDataSnapshot = () => ({}),
        makeEndNodeData = () => ({}),
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        doc = null,
        rebuildAll = () => {}
    } = deps;
    if (!state?.drawflow) return null;
    const data = Object.assign(makeEndNodeData(), options.data || {});
    state.suspendFlowEvents = true;
    let id = null;
    try {
        const count = Object.keys(getActiveFlowDataSnapshot()).length;
        id = state.drawflow.addNode(
            "end",
            1, 0,
            options.x ?? (700 + (count % 5) * 170), options.y ?? (90 + Math.floor(count / 5) * 130),
            "end",
            data,
            buildFlowNodeHtml(data)
        );
        const drawflowRoot = doc?.getElementById?.("drawflow-container") || doc;
        bindConditionNodeSelection(drawflowRoot);
    } finally {
        state.suspendFlowEvents = false;
    }
    rebuildAll();
    return String(id);
}

export function addSubprocessFlowNode(deps = {}) {
    const {
        state,
        options = {},
        getActiveFlowDataSnapshot = () => ({}),
        ensureSubprocessMeta = () => null,
        makeUniqueSubprocessName = (baseName) => baseName,
        ensureDrawflowModule = () => {},
        makeSubprocessNodeData = () => ({}),
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        doc = null,
        renderFlowTabs = () => {},
        rebuildAll = () => {}
    } = deps;
    if (!state?.drawflow) return null;
    const meta = options.meta
        ? ensureSubprocessMeta(options.meta)
        : ensureSubprocessMeta({
            name: makeUniqueSubprocessName("サブプロセス"),
            durationSec: 1
        });
    ensureDrawflowModule(meta.moduleName);
    const data = Object.assign({}, makeSubprocessNodeData(meta), {
        label: meta.name,
        subprocessId: meta.id,
        subprocessName: meta.name,
        durationSec: meta.durationSec
    }, options.data || {});
    state.suspendFlowEvents = true;
    let id = null;
    try {
        const count = Object.keys(getActiveFlowDataSnapshot()).length;
        id = state.drawflow.addNode(
            "subprocess",
            1, 1,
            options.x ?? (340 + (count % 5) * 170), options.y ?? (120 + Math.floor(count / 5) * 130),
            "subprocess",
            data,
            buildFlowNodeHtml(data)
        );
        if (options.prevNodeId) {
            state.drawflow.addConnection(options.prevNodeId, String(id), "output_1", "input_1");
        }
        const drawflowRoot = doc?.getElementById?.("drawflow-container") || doc;
        bindConditionNodeSelection(drawflowRoot);
    } finally {
        state.suspendFlowEvents = false;
    }
    renderFlowTabs();
    rebuildAll();
    return String(id);
}

export function makeSubprocessNodeData(deps = {}) {
    const {
        meta = null,
        ensureSubprocessMeta = () => null,
        makeUniqueSubprocessName = (baseName) => baseName,
        timingModeExpand = "expand"
    } = deps;
    const subprocess = meta || ensureSubprocessMeta({
        name: makeUniqueSubprocessName("サブプロセス"),
        durationSec: 1
    });
    return {
        nodeType: "subprocess",
        label: subprocess?.name,
        subprocessId: subprocess?.id,
        subprocessName: subprocess?.name,
        durationSec: subprocess?.durationSec,
        timingMode: timingModeExpand,
        collapsed: true
    };
}

export function addFlowMotionFromToolbar(deps = {}) {
    const {
        state,
        doc = typeof document !== "undefined" ? document : null,
        addMotion = () => {}
    } = deps;
    const select = doc?.getElementById?.("flowAxisSelect") || null;
    const axisId = select?.value || state?.selectedAxisId;
    addMotion(axisId);
    return axisId || "";
}

export function clearActiveFlowPalette(deps = {}) {
    const {
        state,
        getActiveFlowModuleName = () => "Home",
        getActiveFlowDataSnapshot = () => ({}),
        confirmClear = () => false,
        closeFlowNodeEditor = () => {},
        renderFlowConnectionAssist = () => {},
        rebuildStateFromFlow = () => {},
        renderFlowTabs = () => {},
        scheduleFlowConnectionRefresh = () => {},
        logWarn = (...args) => console.warn(...args)
    } = deps;
    if (!state?.drawflow) return false;
    const moduleName = getActiveFlowModuleName();
    const moduleData = getActiveFlowDataSnapshot();
    const nodeIds = Object.keys(moduleData);
    if (!nodeIds.length) return false;
    const label = moduleName === "Home" ? "メイン" : "現在のサブプロセス";
    if (!confirmClear(`${label}のフローを一括クリアしますか？`)) return false;
    state.selectedFlowNodeId = null;
    state.pendingFlowConnection = null;
    closeFlowNodeEditor();
    renderFlowConnectionAssist();
    for (const nodeId of nodeIds) {
        try {
            state.drawflow.removeNodeId("node-" + nodeId);
        } catch (error) {
            logWarn("flow node clear failed", { nodeId, error });
        }
    }
    rebuildStateFromFlow();
    renderFlowTabs();
    scheduleFlowConnectionRefresh();
    return true;
}

export function removeFlowNode(deps = {}) {
    const {
        state,
        nodeId,
        getActiveFlowModuleName = () => "Home"
    } = deps;
    if (!state?.drawflow) return false;
    state.suspendFlowEvents = true;
    try {
        try { state.drawflow.removeNodeId("node-" + nodeId); } catch (error) { /* ignore */ }
    } finally {
        state.suspendFlowEvents = false;
    }
    if (getActiveFlowModuleName() === "Home") {
        const motionId = state.nodeIdToMotionId?.[String(nodeId)];
        if (motionId) {
            delete state.motionIdToNodeId[motionId];
            delete state.nodeIdToMotionId[String(nodeId)];
        }
    }
    return true;
}

export function bindDrawflowLifecycleEvents(deps = {}) {
    const {
        drawflow = null,
        state,
        handleFlowConnectionCreated = () => false,
        handleFlowConnectionRemoved = () => false,
        handleFlowNodeRemoved = () => false,
        getActiveFlowModuleName = () => "Home",
        getFlowExportSnapshot = () => ({}),
        getFlowModuleData = () => ({}),
        findMotion = () => null,
        countIncomingConnections = () => 0,
        removeFlowConnectionsToMotion = () => {},
        rebuildStateFromFlow = () => {},
        scheduleAutoSave = () => {},
        alertMessage = () => {},
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || "",
        pushTransientWarning = () => {},
        hasSubprocessReferenceNode = () => false,
        removeSubprocessMeta = () => {}
    } = deps;
    if (!drawflow?.on) return false;
    drawflow.on("connectionCreated", (info) => {
        return handleFlowConnectionCreated({
            state,
            info,
            getActiveFlowModuleName,
            getFlowExportSnapshot,
            getFlowModuleData,
            findMotion,
            countIncomingConnections,
            removeFlowConnectionsToMotion,
            rebuildStateFromFlow,
            scheduleAutoSave,
            alertMessage
        });
    });
    drawflow.on("connectionRemoved", (info) => {
        return handleFlowConnectionRemoved({
            state,
            info,
            getActiveFlowModuleName,
            getFlowExportSnapshot,
            rebuildStateFromFlow,
            scheduleAutoSave
        });
    });
    drawflow.on("nodeRemoved", (nodeId) => {
        return handleFlowNodeRemoved({
            state,
            nodeId,
            getActiveFlowModuleName,
            getFlowExportSnapshot,
            getFlowModuleData,
            getNodeData,
            getNodeTypeFromData,
            findMotion,
            pushTransientWarning,
            hasSubprocessReferenceNode,
            removeSubprocessMeta,
            rebuildStateFromFlow,
            scheduleAutoSave
        });
    });
    return true;
}

export function handleFlowConnectionPickClick(deps = {}) {
    const {
        state,
        event,
        getFlowNodeIdFromElement = ({ target }) => target?.id || "",
        getFlowPortName = ({ target, prefix }) => prefix ? String(target?.className || "") : "",
        beginFlowConnection = () => {},
        completeFlowConnection = () => {}
    } = deps;
    if (state?.ui?.flowConnectionMode !== "pick" || !event?.target) return false;
    const outputEl = event.target.closest?.(".output") || null;
    if (outputEl) {
        const nodeId = getFlowNodeIdFromElement({ target: outputEl });
        const outputName = getFlowPortName({ target: outputEl, prefix: "output_" }) || "output_1";
        event.preventDefault?.();
        event.stopPropagation?.();
        beginFlowConnection(nodeId, outputName);
        return true;
    }
    const inputEl = event.target.closest?.(".input") || null;
    if (inputEl) {
        const nodeId = getFlowNodeIdFromElement({ target: inputEl });
        const inputName = getFlowPortName({ target: inputEl, prefix: "input_" }) || "input_1";
        event.preventDefault?.();
        event.stopPropagation?.();
        completeFlowConnection(nodeId, inputName);
        return true;
    }
    return false;
}

export function handleFlowConnectionPickMouseDown(deps = {}) {
    const {
        state,
        event
    } = deps;
    if (state?.ui?.flowConnectionMode !== "pick" || !event?.target) return false;
    if (!event.target.closest?.(".input, .output")) return false;
    event.preventDefault?.();
    event.stopPropagation?.();
    return true;
}

export function handleFlowCanvasClick(deps = {}) {
    const {
        event,
        selectFlowNodeByElement = () => false,
        closeFlowNodeEditor = () => {}
    } = deps;
    const handled = selectFlowNodeByElement(event?.target);
    if (!handled) {
        closeFlowNodeEditor();
    }
    return handled;
}

export function handleFlowConnectionCreated(deps = {}) {
    const {
        state,
        info,
        getActiveFlowModuleName = () => "Home",
        getFlowExportSnapshot = () => ({}),
        getFlowModuleData = () => ({}),
        findMotion = () => null,
        countIncomingConnections = () => 0,
        removeFlowConnectionsToMotion = () => {},
        rebuildStateFromFlow = () => {},
        scheduleAutoSave = () => {},
        alertMessage = () => {}
    } = deps;
    if (!state || state.suspendFlowEvents) return false;
    if (getActiveFlowModuleName() !== "Home") {
        state.project.flow.drawflowData = getFlowExportSnapshot();
        scheduleAutoSave();
        return true;
    }
    const targetMotionId = state.nodeIdToMotionId?.[String(info?.input_id)];
    if (targetMotionId) {
        const target = findMotion(targetMotionId);
        if (target) {
            const exported = state.drawflow?.export?.() || {};
            const targetNode = getFlowModuleData(exported, "Home")?.[state.motionIdToNodeId?.[targetMotionId]];
            const inputCount = countIncomingConnections(targetNode);
            if (inputCount > 1) {
                alertMessage(`「${target.motion.name}」には既に参照元があります。複数入力は未対応のため、新しい接続のみ残します。`);
                removeFlowConnectionsToMotion(targetMotionId, String(info?.output_id));
            }
        }
    }
    rebuildStateFromFlow();
    return true;
}

export function handleFlowConnectionRemoved(deps = {}) {
    const {
        state,
        getActiveFlowModuleName = () => "Home",
        getFlowExportSnapshot = () => ({}),
        rebuildStateFromFlow = () => {},
        scheduleAutoSave = () => {}
    } = deps;
    if (!state || state.suspendFlowEvents) return false;
    if (getActiveFlowModuleName() !== "Home") {
        state.project.flow.drawflowData = getFlowExportSnapshot();
        scheduleAutoSave();
        return true;
    }
    rebuildStateFromFlow();
    return true;
}

export function handleFlowNodeRemoved(deps = {}) {
    const {
        state,
        nodeId,
        getActiveFlowModuleName = () => "Home",
        getFlowExportSnapshot = () => ({}),
        getFlowModuleData = () => ({}),
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || "",
        findMotion = () => null,
        pushTransientWarning = () => {},
        hasSubprocessReferenceNode = () => false,
        removeSubprocessMeta = () => {},
        rebuildStateFromFlow = () => {},
        scheduleAutoSave = () => {}
    } = deps;
    if (!state || state.suspendFlowEvents) return false;
    if (getActiveFlowModuleName() !== "Home") {
        state.project.flow.drawflowData = getFlowExportSnapshot();
        scheduleAutoSave();
        return true;
    }
    const homeDataBeforeRemoval = getFlowModuleData(state.project.flow?.drawflowData, "Home");
    const removedNodeData = getNodeData(homeDataBeforeRemoval?.[String(nodeId)]);
    const motionId = state.nodeIdToMotionId?.[String(nodeId)];
    if (motionId) {
        delete state.nodeIdToMotionId[String(nodeId)];
        delete state.motionIdToNodeId[motionId];
        const found = findMotion(motionId);
        if (found) {
            found.axis.motions = found.axis.motions.filter((motion) => motion.id !== motionId);
            for (const axis of state.project.axes || []) {
                for (const motion of axis.motions || []) {
                    if (motion.dependency && motion.dependency.sourceMotionId === motionId) {
                        motion.dependency = null;
                        if (motion.absoluteStartSec == null) motion.absoluteStartSec = 0;
                        pushTransientWarning(motion.id, "Drawflowでノードが削除されたため参照を未設定に戻しました");
                    }
                }
            }
        }
    }
    if (getNodeTypeFromData(removedNodeData) === "subprocess" && removedNodeData?.subprocessId) {
        const exportedAfterRemoval = getFlowExportSnapshot();
        if (!hasSubprocessReferenceNode(removedNodeData.subprocessId, exportedAfterRemoval)) {
            removeSubprocessMeta(removedNodeData.subprocessId);
        }
    }
    rebuildStateFromFlow();
    return true;
}

export function handleFlowEditorKeydown(deps = {}) {
    const {
        state,
        event,
        clearPendingFlowConnection = () => false
    } = deps;
    if (event?.key !== "Escape" || !state?.pendingFlowConnection) return false;
    clearPendingFlowConnection();
    return true;
}

export function handleFlowCanvasDoubleClick(deps = {}) {
    const {
        state,
        event,
        renderFlowNodeEditor = () => {},
        getActiveFlowDataSnapshot = () => ({}),
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || "",
        findMotion = () => null,
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        requestAnimationFrame = (callback) => callback()
    } = deps;
    const nodeEl = event?.target?.closest?.("[id^='node-']") || null;
    if (!nodeEl) return false;
    const nodeId = String(nodeEl.id || "").replace(/^node-/, "");
    if (!nodeId || !state) return false;
    state.selectedFlowNodeId = nodeId;
    const node = getActiveFlowDataSnapshot()[nodeId];
    const data = getNodeData(node);
    if (getNodeTypeFromData(data) === "motion" && data.motionId) {
        const found = findMotion(data.motionId);
        if (found) {
            state.selectedAxisId = found.axis.id;
            state.selectedMotionId = found.motion.id;
            renderTimingGrid();
            renderSpeedChart();
        }
    }
    renderFlowNodeEditor();
    requestAnimationFrame(() => {
        if (state?.selectedFlowNodeId === nodeId) {
            renderFlowNodeEditor();
        }
    });
    return true;
}

export function toggleSubprocessNodeCollapse(deps = {}) {
    const {
        nodeId,
        getFlowNodeById = () => null,
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || "",
        refreshFlowNodeHtml = () => false,
        selectFlowNode = () => false
    } = deps;
    const node = getFlowNodeById(nodeId);
    const data = getNodeData(node);
    if (getNodeTypeFromData(data) !== "subprocess") return false;
    data.collapsed = !Boolean(data.collapsed);
    refreshFlowNodeHtml(nodeId);
    selectFlowNode(nodeId, { renderEditor: false });
    return true;
}

export function handleFlowNodeAction(deps = {}) {
    const {
        buttonEl,
        event,
        toggleSubprocessNodeCollapse = () => false
    } = deps;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const nodeEl = buttonEl?.closest?.("[id^='node-']") || null;
    const nodeId = String(nodeEl?.id || "").replace(/^node-/, "");
    if (!nodeId) return false;
    const action = buttonEl?.getAttribute?.("data-flow-node-action");
    if (action === "toggle-subprocess-collapse") {
        return toggleSubprocessNodeCollapse(nodeId);
    }
    return false;
}

export function isFlowNodeActionTarget(deps = {}) {
    const {
        target
    } = deps;
    return Boolean(target?.closest?.("[data-flow-node-action]"));
}

export function bindFlowNodeSelectionTargets(deps = {}) {
    const {
        root = null,
        isFlowNodeActionTarget = () => false,
        selectFlowNodeByElement = () => false
    } = deps;
    const layoutElements = root?.querySelectorAll?.(".condition-layout, .button-layout, .error-layout, .signal-output-layout, .subprocess-layout, .end-layout") || [];
    layoutElements.forEach((layoutEl) => {
        if (layoutEl?.dataset?.boundSelect === "1") return;
        layoutEl.dataset.boundSelect = "1";
        const selectCurrentNode = (event) => {
            if (isFlowNodeActionTarget(event?.target)) return;
            selectFlowNodeByElement(layoutEl);
        };
        layoutEl.addEventListener("click", selectCurrentNode);
        layoutEl.addEventListener("pointerup", selectCurrentNode);
    });
    return true;
}

export function bindFlowNodeActions(deps = {}) {
    const {
        root = null,
        handleFlowNodeAction = () => false
    } = deps;
    const buttonElements = root?.querySelectorAll?.("[data-flow-node-action]") || [];
    buttonElements.forEach((buttonEl) => {
        if (buttonEl?.dataset?.boundFlowNodeAction === "1") return;
        buttonEl.dataset.boundFlowNodeAction = "1";
        buttonEl.addEventListener("click", (event) => handleFlowNodeAction(buttonEl, event));
    });
    return true;
}

export function bindConditionNodeSelection(deps = {}) {
    const {
        root = null,
        bindFlowNodeSelectionTargets = () => false,
        bindFlowNodeActions = () => false
    } = deps;
    bindFlowNodeSelectionTargets({ root });
    bindFlowNodeActions({ root });
    return true;
}

export function selectFlowNode(deps = {}) {
    const {
        state,
        nodeId,
        options = {},
        getFlowNodeById = () => null,
        renderFlowNodeEditor = () => {}
    } = deps;
    const nextNodeId = String(nodeId || "");
    if (!nextNodeId) return false;
    const node = getFlowNodeById(nextNodeId);
    if (!node || !state) return false;
    state.selectedFlowNodeId = nextNodeId;
    if (options.renderEditor !== false) {
        renderFlowNodeEditor();
    }
    return true;
}

export function selectFlowNodeByElement(deps = {}) {
    const {
        target,
        getFlowNodeIdFromElement = () => "",
        getFlowNodeById = () => null,
        selectFlowNode = () => false
    } = deps;
    if (target?.closest?.(".input, .output")) return false;
    const nodeId = getFlowNodeIdFromElement(target);
    if (!nodeId) return false;
    const node = getFlowNodeById(nodeId);
    if (!node) return false;
    selectFlowNode(nodeId, { renderEditor: false });
    return true;
}

export function closeFlowNodeEditor(deps = {}) {
    const {
        state,
        renderFlowNodeEditor = () => {}
    } = deps;
    if (!state) return false;
    state.selectedFlowNodeId = null;
    renderFlowNodeEditor();
    return true;
}

export function bindFlowNodeEditorClose(deps = {}) {
    const {
        panel,
        closeFlowNodeEditor = () => {}
    } = deps;
    if (!panel) return false;
    panel.querySelector(".editor-close")?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeFlowNodeEditor();
    });
    return true;
}

export function finishFlowNodeEditor(deps = {}) {
    const {
        panel,
        nodeId,
        bindFlowNodeEditorClose = () => false,
        requestAnimationFrame = (callback) => callback(),
        positionFlowNodeEditor = () => {}
    } = deps;
    if (panel?.dataset) {
        panel.dataset.flowEditorNodeId = String(nodeId || "");
    }
    bindFlowNodeEditorClose(panel);
    requestAnimationFrame(() => positionFlowNodeEditor(nodeId));
    return true;
}

export function positionFlowNodeEditor(deps = {}) {
    const {
        nodeId,
        doc = document,
        viewport = window
    } = deps;
    const panel = doc.getElementById("flowNodeEditor");
    const nodeEl = nodeId ? doc.getElementById("node-" + nodeId) : null;
    if (!panel || !nodeEl || !panel.classList.contains("is-open")) return false;

    const nodeRect = nodeEl.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const gap = 12;
    const viewportPadding = 12;
    let left = nodeRect.right + gap;
    let top = nodeRect.top;

    if (left + panelRect.width > viewport.innerWidth - viewportPadding) {
        left = nodeRect.left - panelRect.width - gap;
    }
    if (left < viewportPadding) left = viewportPadding;
    if (top + panelRect.height > viewport.innerHeight - viewportPadding) {
        top = viewport.innerHeight - panelRect.height - viewportPadding;
    }
    if (top < viewportPadding) top = viewportPadding;

    panel.style.left = left + "px";
    panel.style.top = top + "px";
    return true;
}

export function flowNodeEditorHeader(deps = {}) {
    const {
        title = "",
        escapeHtml = (value) => String(value ?? "")
    } = deps;
    return `
        <div class="editor-header">
            <h3>${escapeHtml(title)}</h3>
            <button type="button" class="secondary editor-close" aria-label="閉じる">×</button>
        </div>
    `;
}

export function bindFlowEditorViewportEvents(deps = {}) {
    const {
        state,
        viewport = window,
        scheduleFlowConnectionRefresh = () => {},
        syncFlowEditorHeight = () => {},
        positionFlowNodeEditor = () => {}
    } = deps;
    if (!viewport?.addEventListener) return false;
    viewport.addEventListener("load", scheduleFlowConnectionRefresh);
    viewport.addEventListener("resize", () => {
        syncFlowEditorHeight();
        scheduleFlowConnectionRefresh();
        positionFlowNodeEditor(state?.selectedFlowNodeId);
    });
    viewport.addEventListener("scroll", () => {
        positionFlowNodeEditor(state?.selectedFlowNodeId);
    }, true);
    return true;
}

export function bindFlowEditorKeydownEvent(deps = {}) {
    const {
        doc = document,
        state,
        handleFlowEditorKeydown = () => false,
        clearPendingFlowConnection = () => false
    } = deps;
    if (!doc?.addEventListener) return false;
    doc.addEventListener("keydown", (event) => {
        handleFlowEditorKeydown({
            state,
            event,
            clearPendingFlowConnection
        });
    });
    return true;
}

export function buildFlowActuatorTypeOptions(deps = {}) {
    const {
        selectedType = "pneumaticCylinder",
        buildActuatorTypeOptions = () => ""
    } = deps;
    return buildActuatorTypeOptions(selectedType);
}

export function makeUniqueActuatorId(deps = {}) {
    const {
        state,
        baseId = ""
    } = deps;
    const list = Array.isArray(state?.project?.actuators) ? state.project.actuators : [];
    const used = new Set(list.map((row) => String(row?.id || "").toLowerCase()));
    let candidate = baseId;
    let index = 2;
    while (used.has(candidate.toLowerCase())) {
        candidate = `${baseId}_${index}`;
        index += 1;
    }
    return candidate;
}

export function makeUniqueActionId(deps = {}) {
    const {
        baseId = "",
        actions = []
    } = deps;
    const used = new Set((Array.isArray(actions) ? actions : []).map((row) => String(row?.id || "").toLowerCase()));
    let candidate = baseId;
    let index = 2;
    while (used.has(candidate.toLowerCase())) {
        candidate = `${baseId}_${index}`;
        index += 1;
    }
    return candidate;
}

export function findActuatorById(deps = {}) {
    const {
        state,
        actuatorId = ""
    } = deps;
    const actuators = Array.isArray(state?.project?.actuators) ? state.project.actuators : [];
    return actuators.find((row) => row.id === actuatorId) || null;
}

export function resolveFlowMotionActuatorType(deps = {}) {
    const {
        data,
        found,
        findActuatorById = () => null,
        normalizeAxisActuatorType = (value) => value ?? ""
    } = deps;
    const actuator = findActuatorById(data?.actuatorId || "");
    if (actuator?.actuatorType) return normalizeAxisActuatorType(actuator.actuatorType);
    return normalizeAxisActuatorType(found?.axis?.actuatorType);
}

export function ensureFlowMotionActuatorBinding(deps = {}) {
    const {
        state,
        data,
        found,
        findActuatorById = () => null,
        createFlowDerivedActuator = () => null,
        mergeActuatorActionsWithDefaults = (actions) => actions,
        normalizeControlSpecText = (value, fallback = "") => value || fallback,
        buildFlowActionSelectionState = buildFlowActionSelectionStateHelper,
        buildFlowActionSelectionStateFallback = (_actions = [], selectedActionId = "") => ({ selectedActionId }),
        escapeHtml = (value) => String(value ?? "")
    } = deps;
    if (!data || !found || !state?.project) return { actuator: null, changed: false };
    if (!Array.isArray(state.project.actuators)) state.project.actuators = [];

    let actuator = findActuatorById(data.actuatorId || "");
    let changed = false;
    if (!actuator) {
        actuator = createFlowDerivedActuator(found.axis, found.motion);
        data.actuatorId = actuator?.id;
        changed = true;
    } else {
        const mergedActions = mergeActuatorActionsWithDefaults(
            actuator.actions,
            actuator.actuatorType,
            found.motion?.name || "",
            found.motion?.id || "",
            actuator.motorControlType || found.axis?.motorControlType || "",
            actuator.controlMethod || ""
        );
        if (JSON.stringify(mergedActions) !== JSON.stringify(actuator.actions || [])) {
            actuator.actions = mergedActions;
            changed = true;
        }
        if (!actuator.name) {
            actuator.name = normalizeControlSpecText(found.motion?.name, normalizeControlSpecText(found.axis?.name, actuator.id));
            changed = true;
        }
    }

    const resolveActionSelectionState = typeof buildFlowActionSelectionState === "function"
        ? buildFlowActionSelectionState
        : buildFlowActionSelectionStateFallback;
    const nextActionId = resolveActionSelectionState(
        Array.isArray(actuator?.actions) ? actuator.actions : [],
        data.actionId || "",
        escapeHtml
    ).selectedActionId;
    if ((data.actionId || "") !== nextActionId) {
        data.actionId = nextActionId;
        changed = true;
    }

    return { actuator, changed };
}

export function createFlowDerivedActuator(deps = {}) {
    const {
        state,
        axis,
        motion,
        normalizeAxisActuatorType = (value) => value ?? "",
        normalizeControlSpecText = (value, fallback = "") => value || fallback,
        buildControlSpecSlug = (value, fallback = "item") => value || fallback,
        makeUniqueActuatorId = (baseId) => baseId,
        normalizeMotorControlType = (value) => value ?? "",
        normalizeActuatorControlMethod = (value) => value ?? "",
        normalizePneumaticValveMode: normalizePneumaticValveModeFn = (value) => value ?? "",
        buildDefaultAxisRef = (name = "", actuatorId = "") => ({ name, actuatorId }),
        mergeActuatorActionsWithDefaults = (actions) => actions
    } = deps;
    if (!state?.project) return null;
    if (!Array.isArray(state.project.actuators)) state.project.actuators = [];
    const actuatorType = normalizeAxisActuatorType(axis?.actuatorType);
    const actuatorName = normalizeControlSpecText(motion?.name, normalizeControlSpecText(axis?.name, "アクチュエータ"));
    const prefixMap = {
        pneumaticCylinder: "pneu",
        hydraulicCylinder: "hyd",
        electricCylinder: "elec",
        motor: "motor",
        chuck: "chuck",
        vacuum: "vac",
        heater: "heater",
        generic: "io"
    };
    const prefix = prefixMap[actuatorType] || "act";
    const baseSlug = buildControlSpecSlug(actuatorName, axis?.id ? `actuator_${buildControlSpecSlug(axis.id, "axis")}` : "actuator");
    const actuatorId = makeUniqueActuatorId(`${prefix}_${baseSlug}`);
    const controlMethod = normalizeActuatorControlMethod("", actuatorType);
    const actuator = {
        id: actuatorId,
        name: actuatorName,
        actuatorType,
        motorControlType: normalizeMotorControlType(axis?.motorControlType, actuatorType),
        pneumaticValveMode: normalizePneumaticValveModeFn("", actuatorType),
        controlMethod,
        axisRef: buildDefaultAxisRef(axis?.name || actuatorName, axis?.id || actuatorId),
        actions: mergeActuatorActionsWithDefaults([], actuatorType, motion?.name || "", motion?.id || "", axis?.motorControlType || "", controlMethod)
    };
    state.project.actuators.push(actuator);
    return actuator;
}

export function applyFlowMotionActuatorType(deps = {}) {
    const {
        data,
        found,
        nextType,
        normalizeAxisActuatorType = (value) => value ?? "",
        findActuatorById = () => null,
        createFlowDerivedActuator = () => null,
        normalizeMotorControlType = (value) => value ?? "",
        normalizeActuatorControlMethod = (value) => value ?? "",
        normalizePneumaticValveMode: normalizePneumaticValveModeFn = (value) => value ?? "",
        buildDefaultAxisRef = (name = "", actuatorId = "") => ({ name, actuatorId }),
        mergeActuatorActionsWithDefaults = (actions) => actions
    } = deps;
    if (!data || !found) return null;
    const actuatorType = normalizeAxisActuatorType(nextType || found.axis?.actuatorType);
    let actuator = findActuatorById(data.actuatorId || "");
    if (!actuator) {
        actuator = createFlowDerivedActuator({ ...found.axis, actuatorType }, found.motion);
        data.actuatorId = actuator?.id;
    }
    if (normalizeAxisActuatorType(actuator.actuatorType) !== actuatorType) {
        actuator.actuatorType = actuatorType;
    }
    actuator.motorControlType = normalizeMotorControlType(actuator.motorControlType, actuatorType);
    actuator.pneumaticValveMode = normalizePneumaticValveModeFn(actuator.pneumaticValveMode, actuatorType);
    actuator.controlMethod = normalizeActuatorControlMethod(actuator.controlMethod, actuatorType);
    if ((actuator.controlMethod === "plcopenMotion" || actuator.controlMethod === "plcopenFluidPower") && !actuator.axisRef) {
        actuator.axisRef = buildDefaultAxisRef(found.axis?.name || actuator.name || "", found.axis?.id || actuator.id || "");
    }
    actuator.actions = mergeActuatorActionsWithDefaults(
        actuator.actions,
        actuatorType,
        found.motion?.name || "",
        found.motion?.id || "",
        actuator.motorControlType || found.axis?.motorControlType || "",
        actuator.controlMethod || ""
    );
    return actuator;
}

export function initializeFlowEditorUi(deps = {}) {
    const {
        root = null,
        bindConditionNodeSelection = () => {},
        renderFlowNodeEditor = () => {},
        renderFlowConnectionAssist = () => {}
    } = deps;
    bindConditionNodeSelection(root);
    renderFlowNodeEditor();
    renderFlowConnectionAssist();
    return true;
}

export function bindFlowEditorInteractionEvents(deps = {}) {
    const {
        container = null,
        doc = null,
        viewport = null,
        state,
        handleFlowCanvasDoubleClick = () => false,
        renderFlowNodeEditor = () => {},
        getActiveFlowDataSnapshot = () => ({}),
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || "",
        findMotion = () => null,
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        bindFlowCanvasPointerEvents = () => false,
        handleFlowConnectionPickMouseDown = () => false,
        handleFlowConnectionPickClick = () => false,
        handleFlowCanvasClick = () => false,
        getFlowNodeIdFromElement = () => "",
        getFlowPortName = () => "",
        beginFlowConnection = () => false,
        completeFlowConnection = () => false,
        selectFlowNodeByElement = () => false,
        closeFlowNodeEditor = () => false,
        bindFlowEditorViewportEvents = () => false,
        scheduleFlowConnectionRefresh = () => {},
        syncFlowEditorHeight = () => {},
        positionFlowNodeEditor = () => false,
        requestAnimationFrame = (callback) => callback(),
        bindFlowEditorKeydownEvent = () => false,
        handleFlowEditorKeydown = () => false,
        clearPendingFlowConnection = () => false
    } = deps;
    if (!container) return false;
    container.addEventListener("dblclick", (event) => {
        return handleFlowCanvasDoubleClick({
            state,
            event,
            renderFlowNodeEditor,
            getActiveFlowDataSnapshot,
            getNodeData,
            getNodeTypeFromData,
            findMotion,
            renderTimingGrid,
            renderSpeedChart,
            requestAnimationFrame
        });
    }, true);
    bindFlowCanvasPointerEvents({
        container,
        state,
        handleFlowConnectionPickMouseDown,
        handleFlowConnectionPickClick,
        handleFlowCanvasClick,
        getFlowNodeIdFromElement,
        getFlowPortName,
        beginFlowConnection,
        completeFlowConnection,
        selectFlowNodeByElement,
        closeFlowNodeEditor
    });
    bindFlowEditorViewportEvents({
        state,
        viewport,
        scheduleFlowConnectionRefresh,
        syncFlowEditorHeight,
        positionFlowNodeEditor
    });
    bindFlowEditorKeydownEvent({
        doc,
        state,
        handleFlowEditorKeydown,
        clearPendingFlowConnection
    });
    return true;
}

export function bindFlowCanvasPointerEvents(deps = {}) {
    const {
        container,
        state,
        handleFlowConnectionPickMouseDown = () => false,
        handleFlowConnectionPickClick = () => false,
        handleFlowCanvasClick = () => false,
        getFlowNodeIdFromElement = () => "",
        getFlowPortName = () => "",
        beginFlowConnection = () => false,
        completeFlowConnection = () => false,
        selectFlowNodeByElement = () => false,
        closeFlowNodeEditor = () => false
    } = deps;
    if (!container?.addEventListener) return false;
    container.addEventListener("mousedown", (event) => {
        handleFlowConnectionPickMouseDown({ state, event });
    }, true);
    container.addEventListener("click", (event) => {
        const handledPick = handleFlowConnectionPickClick({
            state,
            event,
            getFlowNodeIdFromElement: ({ target }) => getFlowNodeIdFromElement(target),
            getFlowPortName: ({ target, prefix }) => getFlowPortName(target, prefix),
            beginFlowConnection,
            completeFlowConnection
        });
        if (handledPick) return;
        return handleFlowCanvasClick({
            event,
            selectFlowNodeByElement,
            closeFlowNodeEditor
        });
    }, true);
    return true;
}

export function prepareFlowNodeEditor(deps = {}) {
    const {
        state,
        doc = document,
        getActiveFlowModuleName = () => "Home",
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || ""
    } = deps;
    const panel = doc.getElementById("flowNodeEditor");
    if (!panel) return null;
    const nodeId = state?.selectedFlowNodeId || null;
    const node = getFlowNodeById({
        state,
        nodeId,
        getActiveFlowModuleName
    });
    if (!node) {
        panel.classList.remove("is-open");
        panel.innerHTML = "";
        if (panel.dataset) {
            panel.dataset.flowEditorNodeId = "";
        }
        return {
            panel,
            nodeId,
            node: null,
            data: null,
            nodeType: ""
        };
    }
    panel.classList.add("is-open");
    const data = getNodeData(node);
    return {
        panel,
        nodeId,
        node,
        data,
        nodeType: getNodeTypeFromData(data)
    };
}

export function renderFlowNodeEditor(deps = {}) {
    const {
        prepareFlowNodeEditor: prepareEditor = prepareFlowNodeEditor,
        renderSelectedFlowNodeEditor: renderSelectedEditor = renderSelectedFlowNodeEditor
    } = deps;
    const context = prepareEditor(deps);
    if (!context?.node) return false;
    const handled = renderSelectedEditor({
        ...deps,
        panel: context.panel,
        nodeId: context.nodeId,
        nodeType: context.nodeType,
        data: context.data
    });
    return Boolean(handled);
}

export function buildFlowNodeEditorDeps(deps = {}) {
    const {
        panel,
        nodeId,
        nodeType = "",
        data,
        escapeHtml = (value) => String(value ?? ""),
        flowNodeEditorHeader = (title) => `<h3>${title}</h3>`,
        finishFlowNodeEditor = () => {},
        refreshFlowNodeHtml = () => {},
        ensureButtonNodeData = () => {},
        ensureSignalOutputData = () => {},
        normalizeButtonMode = (value) => value ?? "",
        syncInternalFlagsFromFlowSnapshot = () => {},
        getInternalFlagList = () => [],
        getConditionMode = () => "simple",
        getConditionKind = () => "signal",
        getConditionOperator = () => "all",
        ensureConditionNodeData = () => {},
        ensureConditionDraftSignal = () => {},
        normalizeConditionEntries = () => [],
        normalizeConditionEntry = (entry) => entry,
        findMotion = () => null,
        applyFlowMotionActuatorType = () => null,
        ensureFlowMotionActuatorBinding = () => ({ changed: false }),
        resolveFlowMotionActuatorType = () => "",
        findActuatorById = () => null,
        buildFlowActuatorTypeOptions = () => "",
        renderControlSpecActuators = () => {},
        recomputeAll = () => {},
        renderControlSpecView = () => {},
        scheduleAutoSave = () => {},
        renderErrors = () => {},
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        updateAllFlowNodeLabels = () => {},
        isControlSpecViewActive = false,
        normalizeSignalScope = (value) => value ?? "",
        commitInternalFlagName = () => {},
        renderFlowTabs = () => {},
        renderFlowNodeEditor = () => {},
        findSubprocessById = () => null,
        ensureSubprocessMeta = () => null,
        getSubprocessList = () => [],
        isSubprocessNameDuplicate = () => false,
        refreshSubprocessReferenceNodes = () => {},
        ensureDrawflowModule = () => {},
        setActiveFlowModule = () => {},
        mountFlowErrorEditor = mountFlowErrorEditorHelper,
        mountFlowButtonEditor = mountFlowButtonEditorHelper,
        mountFlowEndEditor = mountFlowEndEditorHelper,
        mountFlowSignalOutputEditor = mountFlowSignalOutputEditorHelper,
        mountFlowWaitEditor = mountFlowWaitEditorHelper,
        mountFlowSubprocessEditor = mountFlowSubprocessEditorHelper,
        mountFlowConditionEditor = mountFlowConditionEditorHelper,
        buildFlowActionSelectionState = buildFlowActionSelectionStateHelper,
        buildFlowMotionEditorState = buildFlowMotionEditorStateHelper,
        buildFlowMotionEditorStateFallback = ({ found, selectedActuatorType, actionState, selectedAction }) => ({
            found,
            selectedActuatorType,
            actionState,
            selectedAction,
            selectedActionId: actionState?.selectedActionId || ""
        }),
        buildFlowMotionAppliedDataState = buildFlowMotionAppliedDataStateHelper,
        mountFlowMotionEditor = mountFlowMotionEditorHelper,
        actuators = []
    } = deps;
    return {
        panel,
        nodeId,
        nodeType,
        data,
        escapeHtml,
        flowNodeEditorHeader,
        finishFlowNodeEditor,
        refreshFlowNodeHtml,
        ensureButtonNodeData,
        ensureSignalOutputData,
        normalizeButtonMode,
        syncInternalFlagsFromFlowSnapshot,
        getInternalFlagList,
        getConditionMode,
        getConditionKind,
        getConditionOperator,
        ensureConditionNodeData,
        ensureConditionDraftSignal,
        normalizeConditionEntries,
        normalizeConditionEntry,
        findMotion,
        applyFlowMotionActuatorType,
        ensureFlowMotionActuatorBinding,
        resolveFlowMotionActuatorType,
        findActuatorById,
        buildFlowActuatorTypeOptions,
        renderControlSpecActuators,
        recomputeAll,
        renderControlSpecView,
        scheduleAutoSave,
        renderErrors,
        renderTimingGrid,
        renderSpeedChart,
        updateAllFlowNodeLabels,
        isControlSpecViewActive,
        normalizeSignalScope,
        commitInternalFlagName,
        renderFlowTabs,
        renderFlowNodeEditor,
        findSubprocessById,
        ensureSubprocessMeta,
        getSubprocessList,
        isSubprocessNameDuplicate,
        refreshSubprocessReferenceNodes,
        ensureDrawflowModule,
        setActiveFlowModule,
        mountFlowErrorEditor,
        mountFlowButtonEditor,
        mountFlowEndEditor,
        mountFlowSignalOutputEditor,
        mountFlowWaitEditor,
        mountFlowSubprocessEditor,
        mountFlowConditionEditor,
        buildFlowActionSelectionState,
        buildFlowMotionEditorState,
        buildFlowMotionAppliedDataState,
        mountFlowMotionEditor,
        actuators
    };
}

export function renderSelectedFlowNodeEditor(deps = {}) {
    const editorDeps = buildFlowNodeEditorDeps(deps);
    return renderBasicFlowNodeEditor(editorDeps);
}

export function refreshFlowNodeHtml(deps = {}) {
    const {
        nodeId,
        state,
        doc = document,
        getActiveFlowModuleName = () => "Home",
        getNodeData = (node) => node?.data || {},
        findMotion = () => null,
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        scheduleFlowConnectionRefresh = () => {},
        positionFlowNodeEditor = () => {},
        rebuildStateFromFlow = () => {}
    } = deps;
    const node = getFlowNodeById({
        state,
        nodeId,
        getActiveFlowModuleName
    });
    if (!node) return false;
    const elem = doc.getElementById("node-" + nodeId);
    const inner = elem?.querySelector?.(".drawflow_content_node") || null;
    if (inner) {
        const data = getNodeData(node);
        const found = data?.motionId ? findMotion(data.motionId) : null;
        inner.innerHTML = found
            ? buildFlowNodeHtml(data, found.axis, found.motion)
            : buildFlowNodeHtml(data);
        bindConditionNodeSelection(inner);
    }
    scheduleFlowConnectionRefresh();
    positionFlowNodeEditor(nodeId);
    rebuildStateFromFlow({ renderFlowNodeEditor: false });
    return true;
}

export function rebuildStateFromFlow(deps = {}) {
    const {
        state,
        options = {},
        getActiveFlowModuleName = () => "Home",
        getFlowExportSnapshot = () => ({}),
        getFlowModuleData = () => ({}),
        syncInternalFlagsFromFlowSnapshot = () => {},
        scheduleAutoSave = () => {},
        getNodeIncomingConnections = () => [],
        resolveFlowConnection = () => ({}),
        pushTransientWarning = () => {},
        rebuildAll = () => {}
    } = deps;
    if (!state?.drawflow) return false;
    if (getActiveFlowModuleName() !== "Home") {
        state.project.flow.drawflowData = getFlowExportSnapshot();
        scheduleAutoSave();
        return true;
    }
    const exported = state.drawflow.export();
    syncInternalFlagsFromFlowSnapshot(exported);
    const homeData = getFlowModuleData(exported, "Home");
    for (const axis of state.project.axes || []) for (const motion of axis.motions || []) {
        const nodeId = state.motionIdToNodeId[motion.id];
        if (!nodeId) continue;
        const node = homeData[nodeId];
        if (!node) continue;
        const incoming = getNodeIncomingConnections(node);
        const inputCount = incoming.length;
        if (inputCount > 1) {
            pushTransientWarning(motion.id, `複数入力(${inputCount})はv1非対応。1つに減らしてください`);
        }
        if (incoming.length > 0) {
            const resolved = resolveFlowConnection(homeData, incoming[0]);
            if (resolved.warning) pushTransientWarning(motion.id, resolved.warning);
            motion.startConditionText = (resolved.conditionParts || []).join(" / ");
            if (resolved.sourceMotionId) {
                motion.dependency = { sourceMotionId: resolved.sourceMotionId, anchor: "end", offsetSec: motion.dependency?.offsetSec ?? 0 };
                motion.absoluteStartSec = null;
            } else if (resolved.absoluteStartSec != null) {
                motion.dependency = null;
                motion.absoluteStartSec = resolved.absoluteStartSec;
            } else {
                motion.dependency = null;
                if (motion.absoluteStartSec == null) motion.absoluteStartSec = 0;
            }
        } else {
            if (motion.dependency) {
                motion.dependency = null;
                if (motion.absoluteStartSec == null) motion.absoluteStartSec = 0;
            }
            if (!motion.startConditionText) motion.startConditionText = "";
        }
    }
    rebuildAll(options);
    return true;
}

export function rebuildFlowFromState(deps = {}) {
    const {
        state,
        doc = document,
        getActiveFlowModuleName = () => "Home",
        setActiveFlowModule = () => {},
        clearCustomDrawflowModules = () => {},
        buildFlowNodeHtml = () => "",
        bindConditionNodeSelection = () => {},
        renderFlowTabs = () => {},
        getSubprocessList = () => [],
        scheduleFlowConnectionRefresh = () => {}
    } = deps;
    if (!state?.drawflow) return false;
    const previousModule = getActiveFlowModuleName();
    state.suspendFlowEvents = true;
    try {
        setActiveFlowModule("Home");
        clearCustomDrawflowModules();
        state.drawflow.clear();
        state.nodeIdToMotionId = {};
        state.motionIdToNodeId = {};
        let rowIndex = 0;
        for (const axis of state.project.axes || []) {
            let colIndex = 0;
            for (const motion of axis.motions || []) {
                const id = state.drawflow.addNode(
                    "motion", 1, 1,
                    80 + colIndex * 220, 40 + rowIndex * 130,
                    "motion",
                    { axisId: axis.id, motionId: motion.id, nodeType: "motion" },
                    buildFlowNodeHtml({ axisId: axis.id, motionId: motion.id, nodeType: "motion" }, axis, motion)
                );
                state.nodeIdToMotionId[String(id)] = motion.id;
                state.motionIdToNodeId[motion.id] = String(id);
                colIndex++;
            }
            rowIndex++;
        }
        for (const axis of state.project.axes || []) {
            for (const motion of axis.motions || []) {
                if (!motion.dependency?.sourceMotionId) continue;
                const sourceNodeId = state.motionIdToNodeId[motion.dependency.sourceMotionId];
                const targetNodeId = state.motionIdToNodeId[motion.id];
                if (!sourceNodeId || !targetNodeId) continue;
                try {
                    state.drawflow.addConnection(sourceNodeId, targetNodeId, "output_1", "input_1");
                } catch (_error) {}
            }
        }
    } finally {
        state.suspendFlowEvents = false;
    }
    const drawflowRoot = doc.getElementById("drawflow-container") || doc;
    bindConditionNodeSelection(drawflowRoot);
    renderFlowTabs();
    if (previousModule !== "Home" && getSubprocessList().some((item) => item.moduleName === previousModule)) {
        setActiveFlowModule(previousModule);
    }
    scheduleFlowConnectionRefresh();
    return true;
}

export function restoreFlowFromExport(deps = {}) {
    const {
        state,
        exportedData,
        doc = document,
        ensureProjectFlowState = () => ({}),
        clearCustomDrawflowModules = () => {},
        getNodeData = (node) => node?.data || {},
        getNodeTypeFromData = (data) => data?.nodeType || data?.type || "",
        findMotion = () => null,
        buildFlowNodeHtml = () => "",
        getDrawflowAdapter = () => ({ importExport: () => {} }),
        syncInternalFlagsFromFlowSnapshot = () => {},
        getSubprocessList = () => [],
        ensureDrawflowModule = () => {},
        refreshHomeMotionNodeMaps = () => {},
        bindConditionNodeSelection = () => {},
        renderFlowTabs = () => {},
        scheduleFlowConnectionRefresh = () => {}
    } = deps;
    if (!state?.drawflow) return false;
    ensureProjectFlowState(state.project);
    state.suspendFlowEvents = true;
    try {
        clearCustomDrawflowModules();
        state.nodeIdToMotionId = {};
        state.motionIdToNodeId = {};
        const doImport = (data) => {
            const nodeData = getNodeData(data);
            const nodeType = getNodeTypeFromData(nodeData);
            if (nodeType === "signal_output" && data?.class !== "signal-output") {
                data.class = "signal-output";
            }
            const motionId = nodeData.motionId;
            const found = motionId ? findMotion(motionId) : null;
            if (nodeType === "motion" && found) return buildFlowNodeHtml(nodeData, found.axis, found.motion);
            if (nodeType === "start" || nodeType === "condition" || nodeType === "button" || nodeType === "error" || nodeType === "signal_output" || nodeType === "wait" || nodeType === "subprocess" || nodeType === "end") {
                return buildFlowNodeHtml(nodeData);
            }
            return null;
        };
        getDrawflowAdapter().importExport(exportedData, doImport);
        syncInternalFlagsFromFlowSnapshot(exportedData);
        for (const subprocess of getSubprocessList()) {
            ensureDrawflowModule(subprocess.moduleName);
        }
        refreshHomeMotionNodeMaps(exportedData);
    } finally {
        state.suspendFlowEvents = false;
    }
    state.activeFlowModuleName = "Home";
    const drawflowRoot = doc.getElementById("drawflow-container") || doc;
    bindConditionNodeSelection(drawflowRoot);
    renderFlowTabs();
    scheduleFlowConnectionRefresh();
    return true;
}

export function rebuildAll(deps = {}) {
    const {
        state,
        options = {},
        syncActiveFlowNodeEditorState = () => {},
        recomputeAll = () => {},
        refreshActuatorTypeSelectOptions = () => {},
        renderErrors = () => {},
        updateFlowAxisSelect = () => {},
        renderFlowNodeEditor = () => {},
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        renderAssistantInsights = () => {},
        notifyAssistantUiChanged = () => {},
        scheduleAutoSave = () => {}
    } = deps;
    syncActiveFlowNodeEditorState({ refresh: false });
    recomputeAll();
    refreshActuatorTypeSelectOptions();
    renderErrors();
    updateFlowAxisSelect();
    if (options.renderFlowNodeEditor !== false) renderFlowNodeEditor();
    renderTimingGrid();
    renderSpeedChart();
    state.project.flow.drawflowData = state.drawflow ? state.drawflow.export() : {};
    renderAssistantInsights();
    notifyAssistantUiChanged();
    scheduleAutoSave();
    return true;
}

export function importData(deps = {}) {
    const {
        state,
        data,
        genId,
        getPreferredProjectIo = () => ({ normalizeProjectImport: (value) => value }),
        doc = document,
        applyUnitsToUI = () => {},
        getFlowModuleData = () => ({}),
        restoreFlowFromExport = () => {},
        rebuildFlowFromState = () => {},
        renderFlowTabs = () => {},
        rebuildAll = () => {},
        renderFlowConnectionAssist = () => {},
        clearAutoSaveTimer = () => {},
        setChatStatus = () => {},
        alertMessage = () => {},
        logError = () => {}
    } = deps;
    try {
        state.project = getPreferredProjectIo().normalizeProjectImport(data, { genId });
        state.selectedAxisId = state.project.axes[0]?.id ?? null;
        state.selectedMotionId = null;
        state.selectedFlowNodeId = null;
        state.pendingFlowConnection = null;
        state.activeFlowModuleName = "Home";
        const nameInput = doc.getElementById("projectNameInput");
        if (nameInput && state.project.name) nameInput.value = state.project.name;
        applyUnitsToUI();
        const importedSnapshot = state.project.flow?.drawflowData || {};
        if (getFlowModuleData(importedSnapshot, "Home") && Object.keys(getFlowModuleData(importedSnapshot, "Home")).length > 0) {
            restoreFlowFromExport(importedSnapshot);
        } else {
            rebuildFlowFromState();
        }
        renderFlowTabs();
        rebuildAll();
        renderFlowConnectionAssist();
        clearAutoSaveTimer();
        setChatStatus("プロジェクトを読み込みました");
        return true;
    } catch (error) {
        alertMessage("データのインポートに失敗しました: " + error.message);
        logError(error);
        return false;
    }
}

export function exportData(deps = {}) {
    const {
        state,
        syncActiveFlowNodeEditorState = () => {},
        syncInternalFlagsFromFlowSnapshot = () => {},
        getPreferredProjectIo = () => ({ serializeProjectExport: () => "" }),
        BlobCtor = Blob,
        urlApi = URL,
        doc = document
    } = deps;
    syncActiveFlowNodeEditorState({ refresh: false });
    const drawflowSnapshot = state.drawflow ? state.drawflow.export() : {};
    syncInternalFlagsFromFlowSnapshot(drawflowSnapshot);
    state.project.flow.drawflowData = drawflowSnapshot;
    const json = getPreferredProjectIo().serializeProjectExport(state.project, drawflowSnapshot);
    return downloadJsonFile({
        json,
        filename: "multi-axis-timing-chart-data.json",
        BlobCtor,
        urlApi,
        doc
    });
}

export function downloadJsonFile(deps = {}) {
    const {
        json = "",
        filename = "multi-axis-timing-chart-data.json",
        BlobCtor = Blob,
        urlApi = URL,
        doc = document
    } = deps;
    const blob = new BlobCtor([json], { type: "application/json" });
    const url = urlApi.createObjectURL(blob);
    const anchor = doc.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    urlApi.revokeObjectURL(url);
    return true;
}

function sanitizeProjectDownloadFilename(name = "") {
    return String(name || "project").trim().replace(/[\\/:*?"<>|]+/g, "_") || "project";
}

export function bindFlowViewEvents(deps = {}) {
    const {
        refs = {},
        addFlowMotionFromToolbar = () => {},
        addStartNode = () => {},
        addConditionNode = () => {},
        addButtonNode = () => {},
        addErrorNode = () => {},
        addSignalOutputNode = () => {},
        addWaitNode = () => {},
        addSubprocessNode = () => {},
        addEndNode = () => {},
        setFlowConnectionHoverText = () => {},
        setFlowConnectionMode = () => {},
        clearActiveFlowPalette = () => {}
    } = deps;
    refs.btnAddFlowMotion?.addEventListener("click", () => addFlowMotionFromToolbar());
    refs.btnAddStartNode?.addEventListener("click", () => addStartNode());
    refs.btnAddConditionNode?.addEventListener("click", () => addConditionNode());
    refs.btnAddButtonNode?.addEventListener("click", () => addButtonNode());
    refs.btnAddErrorNode?.addEventListener("click", () => addErrorNode());
    refs.btnAddSignalOutputNode?.addEventListener("click", () => addSignalOutputNode());
    refs.btnAddWaitNode?.addEventListener("click", () => addWaitNode());
    refs.btnAddSubprocessNode?.addEventListener("click", () => addSubprocessNode());
    refs.btnAddEndNode?.addEventListener("click", () => addEndNode());
    refs.btnFlowConnectDrag?.addEventListener("click", () => {
        setFlowConnectionHoverText("");
        setFlowConnectionMode("drag");
    });
    refs.btnFlowConnectPick?.addEventListener("click", () => {
        setFlowConnectionHoverText("");
        setFlowConnectionMode("pick");
    });
    [refs.btnFlowConnectDrag, refs.btnFlowConnectPick].forEach((button) => {
        if (!button) return;
        const tooltip = button.getAttribute("data-flow-tooltip") || "";
        button.addEventListener("mouseenter", () => setFlowConnectionHoverText(tooltip));
        button.addEventListener("focus", () => setFlowConnectionHoverText(tooltip));
        button.addEventListener("mouseleave", () => setFlowConnectionHoverText(""));
        button.addEventListener("blur", () => setFlowConnectionHoverText(""));
    });
    refs.btnClearFlowPalette?.addEventListener("click", () => clearActiveFlowPalette());
    return true;
}

export function bindFlowAxisSelector(deps = {}) {
    const {
        doc = document,
        state,
        getFlowAxisPopoverOpen = () => false,
        setFlowAxisPopoverOpenState = () => {},
        addAxis = () => {},
        getSelectValue = () => "",
        getNextAxisDefaultName = () => "",
        removeAxis = () => {},
        setTimeoutFn = (fn) => setTimeout(fn, 0)
    } = deps;

    function setFlowAxisPopoverOpen(open) {
        const nextOpen = Boolean(open);
        setFlowAxisPopoverOpenState(nextOpen);
        const popover = doc.getElementById("flowAxisAddPopover");
        const button = doc.getElementById("btnFlowAddAxis");
        const input = doc.getElementById("flowAxisAddNameInput");
        if (!popover || !button) return;
        popover.classList.toggle("is-open", nextOpen);
        button.setAttribute("aria-expanded", String(nextOpen));
        if (nextOpen && input) {
            if (!input.value) input.value = getNextAxisDefaultName();
            setTimeoutFn(() => {
                input.focus();
                input.select();
            }, 0);
            return;
        }
        if (!nextOpen && input) {
            input.value = "";
            const typeSelect = doc.getElementById("flowAxisAddActuatorTypeSelect");
            if (typeSelect) typeSelect.value = "pneumaticCylinder";
            const motorSelect = doc.getElementById("flowAxisAddMotorControlTypeSelect");
            if (motorSelect) motorSelect.value = "speedControl";
        }
    }

    function submitFlowAxis(name) {
        addAxis(String(name || "").trim() || null, {
            actuatorType: getSelectValue("flowAxisAddActuatorTypeSelect"),
            motorControlType: getSelectValue("flowAxisAddMotorControlTypeSelect", "speedControl")
        });
        setFlowAxisPopoverOpen(false);
    }

    doc.getElementById("btnFlowAddAxis")?.addEventListener("click", (event) => {
        event.stopPropagation();
        setFlowAxisPopoverOpen(!getFlowAxisPopoverOpen());
    });
    doc.getElementById("btnFlowAxisAddCancel")?.addEventListener("click", () => {
        setFlowAxisPopoverOpen(false);
    });
    doc.getElementById("btnFlowAxisAddConfirm")?.addEventListener("click", () => {
        const input = doc.getElementById("flowAxisAddNameInput");
        submitFlowAxis(input?.value || "");
    });
    doc.querySelectorAll("[data-flow-axis-preset]").forEach((button) => {
        button.addEventListener("click", () => {
            submitFlowAxis(button.getAttribute("data-flow-axis-preset") || "");
        });
    });
    doc.getElementById("flowAxisAddNameInput")?.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            submitFlowAxis(event.target.value || "");
        } else if (event.key === "Escape") {
            setFlowAxisPopoverOpen(false);
        }
    });
    doc.getElementById("btnFlowRemoveAxis")?.addEventListener("click", () => {
        const axisId = state.selectedAxisId || doc.getElementById("flowAxisSelect")?.value;
        if (axisId) removeAxis(axisId);
    });
    doc.addEventListener("click", (event) => {
        if (!getFlowAxisPopoverOpen()) return;
        const popover = doc.getElementById("flowAxisAddPopover");
        const trigger = doc.getElementById("btnFlowAddAxis");
        if (popover?.contains(event.target) || trigger?.contains(event.target)) return;
        setFlowAxisPopoverOpen(false);
    });
    return true;
}

export function bindTimingViewEvents(deps = {}) {
    const {
        refs = {},
        timingGrid = null,
        state,
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        convertProjectLinearUnits = () => {},
        convertProjectRotaryUnits = () => {},
        rebuildAll = () => {},
        saveAccountSettingsToCloud = () => {}
    } = deps;
    refs.flowAxisSelect?.addEventListener("change", (event) => {
        state.selectedAxisId = event.target.value || null;
        renderTimingGrid();
        renderSpeedChart();
    });
    timingGrid?.addEventListener("change", (event) => {
        if (event.target?.id !== "pxPerSec") return;
        const value = parseInt(event.target.value, 10);
        if (isFinite(value) && value > 0) {
            state.pxPerSec = value;
            renderTimingGrid();
        }
    });
    refs.distanceLinearUnit?.addEventListener("change", (event) => {
        convertProjectLinearUnits("distance", event.target.value);
        rebuildAll();
    });
    refs.accelLinearUnit?.addEventListener("change", (event) => {
        convertProjectLinearUnits("accel", event.target.value);
        rebuildAll();
    });
    refs.speedLinearUnit?.addEventListener("change", (event) => {
        convertProjectLinearUnits("speed", event.target.value);
        rebuildAll();
    });
    refs.angleRotaryUnit?.addEventListener("change", (event) => {
        convertProjectRotaryUnits("angle", event.target.value);
        rebuildAll();
    });
    refs.accelRotaryUnit?.addEventListener("change", (event) => {
        convertProjectRotaryUnits("accel", event.target.value);
        rebuildAll();
    });
    refs.speedRotaryUnit?.addEventListener("change", (event) => {
        convertProjectRotaryUnits("speed", event.target.value);
        rebuildAll();
    });
    refs.btnSaveAccountSettings?.addEventListener("click", () => saveAccountSettingsToCloud());
    return true;
}

export function syncFlowEditorHeight(deps = {}) {
    const {
        doc = document,
        viewport = window
    } = deps;
    const view = doc.getElementById("view-flow");
    const container = doc.getElementById("drawflow-container");
    if (!view || !container || !view.classList.contains("is-active")) return false;
    if (viewport.matchMedia("(max-width: 1180px)").matches) {
        container.style.height = "";
        return true;
    }
    const assistantRail = doc.querySelector(".assistant-rail");
    const containerRect = container.getBoundingClientRect();
    const fallbackBottom = viewport.innerHeight - 16;
    const railBottom = assistantRail?.getBoundingClientRect?.().bottom;
    const targetBottom = Number.isFinite(railBottom) ? Math.min(railBottom, fallbackBottom) : fallbackBottom;
    const nextHeight = Math.max(460, Math.floor(targetBottom - containerRect.top - 12));
    container.style.height = nextHeight + "px";
    return true;
}

export function bindProjectEvents(deps = {}) {
    const {
        refs = {},
        state,
        exportData = () => {},
        importData = () => {},
        scheduleAutoSave = () => {},
        confirmFn = () => true,
        clearTimeoutFn = () => {},
        getAutoSaveTimer = () => null,
        setCloudProjectId = () => {},
        emptyProject = () => ({}),
        applyAccountSettingsDefaultsToProject = () => {},
        updateCloudSaveStatus = () => {},
        clearCustomDrawflowModules = () => {},
        setActiveFlowModule = () => {},
        refreshHomeMotionNodeMaps = () => {},
        rebuildAll = () => {},
        renderFlowTabs = () => {},
        renderFlowConnectionAssist = () => {},
        refreshCloudAuthUi = () => {},
        apiProjectsClient = null,
        createElement = (tagName) => document.createElement(tagName),
        createReader = () => new FileReader(),
        loadProjectFromCloud = async () => {},
        saveProjectToCloud = () => {},
        refreshCloudProjectList = () => {},
        signInWithEmail = () => {},
        signOutCloud = () => {},
        openCloudMemberManager = async () => {},
        downloadJson = downloadJsonFile,
        escapeHtml = (value) => String(value ?? ""),
        doc = document,
        DateCtor = Date,
        stringifyJson = (value) => JSON.stringify(value, null, 2)
    } = deps;
    refs.btnExport?.addEventListener("click", exportData);
    refs.importFile?.addEventListener("change", (event) => {
        const file = event.target?.files?.[0];
        if (!file) return;
        const reader = createReader();
        reader.onload = (loadEvent) => importData(JSON.parse(loadEvent.target.result));
        reader.readAsText(file);
    });
    refs.projectNameInput?.addEventListener("input", (event) => {
        state.project.name = event.target.value;
        scheduleAutoSave();
    });
    const createBlankProjectButton = refs.btnCreateBlankProject || refs.btnNewProject;
    createBlankProjectButton?.addEventListener("click", () => {
        if (!confirmFn("新しいプロジェクトを作成しますか？（保存されていない変更は失われます）")) return;
        clearTimeoutFn(getAutoSaveTimer());
        setCloudProjectId(null);
        state.project = emptyProject();
        if (state.accountSettings?.settings_json) {
            applyAccountSettingsDefaultsToProject(state.project, state.accountSettings.settings_json);
        }
        state.selectedAxisId = null;
        state.selectedMotionId = null;
        state.selectedFlowNodeId = null;
        state.pendingFlowConnection = null;
        state.activeFlowModuleName = "Home";
        if (refs.projectNameInput) refs.projectNameInput.value = state.project.name;
        updateCloudSaveStatus("");
        if (state.drawflow) {
            clearCustomDrawflowModules();
            setActiveFlowModule("Home");
            state.suspendFlowEvents = true;
            try {
                state.drawflow.clear();
            } finally {
                state.suspendFlowEvents = false;
            }
            refreshHomeMotionNodeMaps({ drawflow: { Home: { data: {} } } });
        }
        rebuildAll();
        renderFlowTabs();
        renderFlowConnectionAssist();
        refreshCloudAuthUi();
        if (refs.newProjectChoiceModal) refs.newProjectChoiceModal.hidden = true;
    });
    refs.btnProjectList?.addEventListener("click", async (event) => {
        const button = event.currentTarget;
        const panel = refs.projectListPanel;
        if (!button || !panel) return;
        const isOpen = panel.style.display !== "none";
        if (isOpen) {
            panel.style.display = "none";
            button.setAttribute("aria-expanded", "false");
            return;
        }
        panel.style.display = "";
        button.setAttribute("aria-expanded", "true");
        panel.innerHTML = "<div class='project-list-empty'>読み込み中...</div>";

        if (!apiProjectsClient) {
            panel.innerHTML = "<div class='project-list-empty'>ログインが必要です</div>";
            return;
        }
        let accessToken = null;
        try {
            accessToken = await apiProjectsClient.getAccessToken();
        } catch {}
        if (!accessToken) {
            panel.innerHTML = "<div class='project-list-empty'>ログインが必要です</div>";
            return;
        }

        try {
            const list = await apiProjectsClient.listProjects({ accessToken, limit: 50 });
            if (!list.items.length) {
                panel.innerHTML = "<div class='project-list-empty'>保存済みプロジェクトはありません</div>";
                return;
            }
            panel.innerHTML = "";
            for (const row of list.items) {
                const item = createElement("div");
                item.className = "project-list-item";
                const dateStr = row.updated_at ? new DateCtor(row.updated_at).toLocaleDateString("ja-JP") : "";
                item.innerHTML = `<span class="project-list-item-name">${escapeHtml(row.name)}</span>`
                    + `<span class="project-list-item-date">${escapeHtml(dateStr)}</span>`
                    + `<button class="project-list-item-load" data-project-id="${escapeHtml(row.id)}">ロード</button>`
                    + `<button class="project-list-item-download" data-project-download-id="${escapeHtml(row.id)}" data-project-name="${escapeHtml(row.name)}" title="JSONダウンロード" aria-label="JSONダウンロード">↓</button>`;
                panel.appendChild(item);
            }
            panel.addEventListener("click", async (panelEvent) => {
                const loadButton = panelEvent.target?.closest?.("[data-project-id]");
                const downloadButton = panelEvent.target?.closest?.("[data-project-download-id]");
                if (!loadButton && !downloadButton) return;
                panel.style.display = "none";
                button.setAttribute("aria-expanded", "false");
                if (loadButton) {
                    await loadProjectFromCloud(loadButton.dataset.projectId);
                    return;
                }
                let currentAccessToken = null;
                try {
                    currentAccessToken = await apiProjectsClient.getAccessToken();
                } catch {}
                if (!currentAccessToken || !downloadButton) return;
                const row = await apiProjectsClient.getProject({
                    accessToken: currentAccessToken,
                    projectId: downloadButton.dataset.projectDownloadId
                });
                downloadJson({
                    json: stringifyJson(row?.project_json || {}),
                    filename: `${sanitizeProjectDownloadFilename(downloadButton.dataset.projectName || row?.name)}.json`
                });
            }, { once: true });
        } catch (error) {
            panel.innerHTML = `<div class='project-list-empty'>取得失敗: ${escapeHtml(error.message)}</div>`;
        }
    });
    doc.addEventListener("click", (event) => {
        if (!event.target.closest("#btnProjectList") && !event.target.closest("#projectListPanel")) {
            if (refs.projectListPanel && refs.projectListPanel.style.display !== "none") {
                refs.projectListPanel.style.display = "none";
                refs.btnProjectList?.setAttribute("aria-expanded", "false");
            }
        }
    });
    refs.btnCloudSave?.addEventListener("click", () => saveProjectToCloud());
    refs.btnCloudLoad?.addEventListener("click", () => refreshCloudProjectList());
    refs.cloudProjectSelect?.addEventListener("change", (event) => {
        const value = event.target.value;
        if (value) loadProjectFromCloud(value);
    });
    refs.btnCloudSignIn?.addEventListener("click", () => signInWithEmail());
    refs.btnAuthRequiredSignIn?.addEventListener("click", () => signInWithEmail());
    refs.btnCloudSignOut?.addEventListener("click", () => signOutCloud());
    refs.btnCloudManageMembers?.addEventListener("click", () => {
        void openCloudMemberManager();
    });
    refreshCloudAuthUi();
    return true;
}

export function bindToolbar(deps = {}) {
    const {
        bindFlowViewEvents = () => {},
        bindTimingViewEvents = () => {},
        bindProjectEvents = () => {}
    } = deps;
    bindFlowViewEvents();
    bindTimingViewEvents();
    bindProjectEvents();
    return true;
}

export function refreshCloudAuthUi(deps = {}) {
    const {
        refs = {},
        state,
        apiProjectsClient = null,
        setCloudSignedIn = () => {},
        getCloudProjectId = () => null,
        loadAccountSettingsFromCloud = () => {},
        warn = () => {}
    } = deps;
    const status = refs.cloudAuthStatus;
    const workspaceTarget = refs.workspaceTargetStatus;
    const btnSave = refs.btnCloudSave;
    const btnLoad = refs.btnCloudLoad;
    const select = refs.cloudProjectSelect;
    const btnSignIn = refs.btnCloudSignIn;
    const btnSignOut = refs.btnCloudSignOut;
    const btnManageMembers = refs.btnCloudManageMembers;
    const organizationSwitcher = refs.cloudOrganizationSwitcher;
    const userButton = refs.cloudUserButton;
    const btnSaveAcc = refs.btnSaveAccountSettings;
    const authRequiredOverlay = refs.authRequiredOverlay;
    const authRequiredMessage = refs.authRequiredMessage;
    const btnAuthRequiredSignIn = refs.btnAuthRequiredSignIn;
    const projectNameInput = refs.projectNameInput;
    const btnNewProject = refs.btnNewProject || refs.btnCreateBlankProject;
    const btnProjectList = refs.btnProjectList;
    if (!status || !btnSignIn || !btnSignOut) return false;

    const setWorkspaceTarget = ({ visible, label = "" }) => {
        if (!workspaceTarget) return;
        workspaceTarget.hidden = !visible;
        workspaceTarget.textContent = visible ? `対象: ${label}` : "";
        workspaceTarget.title = visible ? `保存・読込・生成の対象ワークスペース: ${label}` : "";
    };
    const canManageMembers = (session) =>
        Boolean(
            session?.organization
            && typeof session?.orgRole === "string"
            && session.orgRole !== "org:member"
        );

    const setUsageLock = ({ locked, message, allowSignIn }) => {
        if (authRequiredOverlay) authRequiredOverlay.hidden = !locked;
        if (authRequiredMessage && typeof message === "string") authRequiredMessage.textContent = message;
        if (btnAuthRequiredSignIn) {
            btnAuthRequiredSignIn.style.display = allowSignIn ? "" : "none";
            btnAuthRequiredSignIn.disabled = !allowSignIn;
        }
        if (projectNameInput) projectNameInput.disabled = locked;
        if (btnNewProject) btnNewProject.disabled = locked;
        if (btnProjectList) btnProjectList.disabled = locked;
        if (locked && refs.projectListPanel) {
            refs.projectListPanel.style.display = "none";
            btnProjectList?.setAttribute("aria-expanded", "false");
        }
    };

    if (!apiProjectsClient || typeof apiProjectsClient.getSession !== "function") {
        setCloudSignedIn(false);
        status.textContent = "アカウント: 未接続 (Clerk 未設定)";
        status.style.color = "#999";
        setWorkspaceTarget({ visible: false });
        if (btnSave) btnSave.disabled = true;
        if (btnLoad) btnLoad.disabled = true;
        if (select) select.style.display = "none";
        if (organizationSwitcher) organizationSwitcher.style.display = "none";
        if (userButton) userButton.style.display = "none";
        if (btnManageMembers) btnManageMembers.style.display = "none";
        btnSignIn.style.display = "none";
        btnSignOut.style.display = "none";
        setUsageLock({
            locked: true,
            message: "このアプリを使うには認証機能の設定が必要です。",
            allowSignIn: false
        });
        return true;
    }

    return Promise.resolve(apiProjectsClient.getSession())
        .then((session) => {
            if (session && session.user) {
                setCloudSignedIn(true);
                const workspaceLabel = session.organization?.name || "個人";
                const accountLabel = session.organization?.name
                    ? `${session.user.email || session.user.displayName || session.user.id} / ${session.organization.name}`
                    : (session.user.email || session.user.displayName || session.user.id);
                status.textContent = `アカウント: ${accountLabel}` + (getCloudProjectId() ? " (保存済み)" : "");
                status.style.color = "#2a7a2a";
                setWorkspaceTarget({ visible: true, label: workspaceLabel });
                if (btnSave) {
                    btnSave.disabled = false;
                    btnSave.title = "現在のプロジェクトをクラウドに保存";
                }
                if (btnLoad) {
                    btnLoad.disabled = false;
                    btnLoad.title = "クラウドのプロジェクト一覧を取得";
                }
                btnSignIn.style.display = "none";
                btnSignOut.style.display = "none";
                if (btnManageMembers) {
                    btnManageMembers.style.display = canManageMembers(session) ? "" : "none";
                }
                if (organizationSwitcher) organizationSwitcher.style.display = "";
                if (userButton) userButton.style.display = "";
                setUsageLock({
                    locked: false,
                    message: "",
                    allowSignIn: true
                });
                Promise.resolve(apiProjectsClient.syncAuthUi?.({
                    userButtonNode: userButton,
                    organizationSwitcherNode: organizationSwitcher
                })).catch(() => {});

                if (!state.accountSettings) {
                    loadAccountSettingsFromCloud();
                } else if (btnSaveAcc) {
                    btnSaveAcc.disabled = false;
                    btnSaveAcc.title = "現在の設定をアカウントのデフォルトとして保存";
                }
            } else {
                setCloudSignedIn(false);
                status.textContent = "アカウント: 未ログイン";
                status.style.color = "#999";
                setWorkspaceTarget({ visible: false });
                if (btnSave) {
                    btnSave.disabled = true;
                    btnSave.title = "ログインが必要です";
                }
                if (btnLoad) {
                    btnLoad.disabled = true;
                    btnLoad.title = "ログインが必要です";
                }
                if (select) select.style.display = "none";
                btnSignIn.style.display = "";
                btnSignOut.style.display = "none";
                if (btnManageMembers) btnManageMembers.style.display = "none";
                if (organizationSwitcher) organizationSwitcher.style.display = "none";
                if (userButton) userButton.style.display = "none";
                setUsageLock({
                    locked: true,
                    message: "このアプリを使うにはサインアップまたはログインが必要です。",
                    allowSignIn: true
                });
                Promise.resolve(apiProjectsClient.syncAuthUi?.({
                    userButtonNode: userButton,
                    organizationSwitcherNode: organizationSwitcher
                })).catch(() => {});

                if (btnSaveAcc) {
                    btnSaveAcc.disabled = true;
                    btnSaveAcc.title = "ログインが必要です";
                }
                state.accountSettings = null;
            }
            return true;
        })
        .catch((error) => {
            warn("[phase4] getSession failed", error);
            setCloudSignedIn(false);
            status.textContent = "アカウント: セッション取得失敗";
            status.style.color = "#a55";
            if (btnManageMembers) btnManageMembers.style.display = "none";
            setWorkspaceTarget({ visible: false });
            setUsageLock({
                locked: true,
                message: "認証状態を確認できませんでした。もう一度サインアップまたはログインをお試しください。",
                allowSignIn: true
            });
            return false;
        });
}

export async function saveProjectToCloud(deps = {}) {
    const {
        apiProjectsClient = null,
        setChatStatus = () => {},
        syncActiveFlowNodeEditorState = () => {},
        state,
        syncInternalFlagsFromFlowSnapshot = () => {},
        getPreferredProjectIo = () => ({ serializeProjectExport: () => "{}" }),
        refreshCloudAuthUi = () => {},
        getCloudProjectId = () => null,
        setCloudProjectId = () => {},
        rememberLastCloudProjectId = () => {},
        updateCloudSaveStatus = () => {},
        warn = () => {}
    } = deps;
    if (!apiProjectsClient) return false;
    setChatStatus("クラウド保存中...");
    syncActiveFlowNodeEditorState({ refresh: false });
    const drawflowSnapshot = state.drawflow ? state.drawflow.export() : {};
    syncInternalFlagsFromFlowSnapshot(drawflowSnapshot);
    state.project.flow.drawflowData = drawflowSnapshot;
    const projectJson = JSON.parse(
        getPreferredProjectIo().serializeProjectExport(state.project, drawflowSnapshot)
    );
    let accessToken = null;
    try {
        accessToken = await apiProjectsClient.getAccessToken();
    } catch {
        accessToken = null;
    }
    if (!accessToken) {
        setChatStatus("クラウド保存にはログインが必要です");
        await refreshCloudAuthUi();
        return false;
    }

    try {
        const currentProjectId = getCloudProjectId();
        const name = state.project.name || projectJson?.metadata?.name || "新しいプロジェクト";
        const result = currentProjectId
            ? await apiProjectsClient.updateProject({
                accessToken, projectId: currentProjectId, fields: { name, projectJson }
            })
            : await apiProjectsClient.createProject({
                accessToken, name, projectJson
            });
        const nextProjectId = result?.id ?? currentProjectId;
        setCloudProjectId(nextProjectId);
        rememberLastCloudProjectId(nextProjectId);
        updateCloudSaveStatus("✓ 保存済み");
        setChatStatus(
            nextProjectId
                ? `クラウド保存しました (${nextProjectId.slice(0, 8)}...)`
                : "クラウド保存しました"
        );
        await refreshCloudAuthUi();
        return true;
    } catch (error) {
        warn("[phase4] cloud save failed", error);
        updateCloudSaveStatus("⚠ 保存失敗");
        setChatStatus(`クラウド保存に失敗: ${error.message || "unknown"}`);
        return false;
    }
}

export async function refreshCloudProjectList(deps = {}) {
    const {
        apiProjectsClient = null,
        select = null,
        setChatStatus = () => {},
        refreshCloudAuthUi = () => {},
        createElement = (tagName) => document.createElement(tagName),
        DateCtor = Date,
        warn = () => {}
    } = deps;
    if (!apiProjectsClient || !select) return false;
    setChatStatus("クラウドのプロジェクト一覧を取得中...");
    let accessToken = null;
    try {
        accessToken = await apiProjectsClient.getAccessToken();
    } catch {
        accessToken = null;
    }
    if (!accessToken) {
        setChatStatus("クラウド読み込みにはログインが必要です");
        await refreshCloudAuthUi();
        return false;
    }

    try {
        const list = await apiProjectsClient.listProjects({ accessToken, limit: 50 });
        select.innerHTML = "";
        const placeholder = createElement("option");
        placeholder.value = "";
        placeholder.textContent = `-- ${list.items.length} 件 --`;
        select.appendChild(placeholder);
        for (const row of list.items) {
            const option = createElement("option");
            option.value = row.id;
            const updated = row.updated_at ? new DateCtor(row.updated_at).toLocaleString() : "";
            option.textContent = `${row.name} ${updated ? "(" + updated + ")" : ""}`;
            select.appendChild(option);
        }
        select.style.display = "";
        setChatStatus(`プロジェクト ${list.items.length} 件を取得しました。選択して再度クラウド読込を押してください`);
        return true;
    } catch (error) {
        warn("[phase4] cloud list failed", error);
        setChatStatus(`クラウド一覧の取得に失敗: ${error.message || "unknown"}`);
        return false;
    }
}

export async function loadProjectFromCloud(deps = {}) {
    const {
        apiProjectsClient = null,
        projectId = "",
        setChatStatus = () => {},
        refreshCloudAuthUi = () => {},
        importData = () => {},
        state,
        notifyAssistantUiChanged = () => {},
        loadAndRenderSkills = () => {},
        setCloudProjectId = () => {},
        rememberLastCloudProjectId = () => {},
        updateCloudSaveStatus = () => {},
        refs = {},
        warn = () => {}
    } = deps;
    if (!apiProjectsClient) return false;
    setChatStatus("クラウドから読み込み中...");
    let accessToken = null;
    try {
        accessToken = await apiProjectsClient.getAccessToken();
    } catch {
        accessToken = null;
    }
    if (!accessToken) {
        setChatStatus("クラウド読み込みにはログインが必要です");
        await refreshCloudAuthUi();
        return false;
    }

    try {
        const row = await apiProjectsClient.getProject({ accessToken, projectId });
        if (!row?.project_json) {
            setChatStatus("プロジェクトのデータが空です");
            return false;
        }
        importData(row.project_json);
        state.project.skillIds = Array.isArray(row.skillIds) ? row.skillIds : [];
        if (typeof state.chat !== "undefined") {
            state.chat.skillCount = state.project.skillIds.length;
            notifyAssistantUiChanged();
        }
        if (state.ui.activeView === "skills") {
            loadAndRenderSkills();
        }
        setCloudProjectId(row.id);
        rememberLastCloudProjectId(row.id);
        const loadedName = row.name || state.project.name || "新しいプロジェクト";
        state.project.name = loadedName;
        if (refs.projectNameInput) refs.projectNameInput.value = loadedName;
        updateCloudSaveStatus("✓ 保存済み");
        setChatStatus(`クラウドから読み込みました (${row.name})`);
        await refreshCloudAuthUi();
        return true;
    } catch (error) {
        warn("[phase4] cloud load failed", error);
        setChatStatus(`クラウド読み込みに失敗: ${error.message || "unknown"}`);
        return false;
    }
}

export async function bootRestoreLastCloudProject(deps = {}) {
    const {
        getBootProjectRestoreDone = () => false,
        setBootProjectRestoreDone = () => {},
        getCloudProjectId = () => null,
        projectHasContent = () => false,
        storage = null,
        storageKey = "",
        apiProjectsClient = null,
        setCloudSignedIn = () => {},
        readStoredProjectId = ({ storage, storageKey }) => {
            try {
                return storage?.getItem?.(storageKey) ?? null;
            } catch {
                return null;
            }
        },
        loadProjectFromCloud = async () => {}
    } = deps;
    if (getBootProjectRestoreDone()) return false;
    if (getCloudProjectId() || projectHasContent()) {
        setBootProjectRestoreDone(true);
        return false;
    }
    if (!apiProjectsClient || typeof apiProjectsClient.getSession !== "function") return false;
    let session = null;
    try {
        session = await apiProjectsClient.getSession();
    } catch {
        session = null;
    }
    if (!session?.user) {
        setBootProjectRestoreDone(true);
        return false;
    }
    const lastId = await readStoredProjectId({ session, storage, storageKey });
    if (!lastId) {
        setBootProjectRestoreDone(true);
        return false;
    }
    setBootProjectRestoreDone(true);
    setCloudSignedIn(true);
    await loadProjectFromCloud(lastId);
    return true;
}

export async function signInWithEmail(deps = {}) {
    const {
        apiProjectsClient = null,
        setChatStatus = () => {},
        warn = () => {}
    } = deps;
    if (!apiProjectsClient || typeof apiProjectsClient.signInWithEmail !== "function") return false;
    setChatStatus("サインアップ / ログイン画面を開いています...");
    try {
        await apiProjectsClient.signInWithEmail();
        setChatStatus("サインアップ / ログイン画面を開きました");
        return true;
    } catch (error) {
        warn("[phase4] sign-in failed", error);
        setChatStatus(`サインアップ / ログイン開始に失敗: ${error.message || "unknown"}`);
        return false;
    }
}

export async function signOutCloud(deps = {}) {
    const {
        apiProjectsClient = null,
        setCloudProjectId = () => {},
        setCloudSignedIn = () => {},
        setBootProjectRestoreDone = () => {},
        rememberLastCloudProjectId = () => {},
        setChatStatus = () => {},
        refreshCloudAuthUi = async () => {},
        warn = () => {}
    } = deps;
    if (!apiProjectsClient || typeof apiProjectsClient.signOut !== "function") return false;
    try {
        await apiProjectsClient.signOut();
        setCloudProjectId(null);
        setCloudSignedIn(false);
        setBootProjectRestoreDone(true);
        rememberLastCloudProjectId(null);
        setChatStatus("ログアウトしました");
        await refreshCloudAuthUi();
        return true;
    } catch (error) {
        warn("[phase4] sign-out failed", error);
        setChatStatus(`ログアウトに失敗: ${error.message || "unknown"}`);
        return false;
    }
}

export function syncBasicFlowNodeEditorState(deps = {}) {
    const {
        panel,
        data,
        nodeType = "",
        normalizeSignalScope = (value) => value ?? "",
        ensureSignalOutputData = () => {},
        commitInternalFlagName = () => {},
        findSubprocessById = () => null,
        isSubprocessNameDuplicate = () => false,
        normalizeButtonMode = (value) => value ?? "",
        buildFlowSignalOutputAppliedDataState = buildFlowSignalOutputAppliedDataStateHelper,
        buildFlowWaitSyncState = buildFlowWaitSyncStateHelper,
        buildFlowSubprocessSyncState = buildFlowSubprocessSyncStateHelper,
        buildFlowErrorSyncState = buildFlowErrorSyncStateHelper,
        buildFlowButtonSyncState = buildFlowButtonSyncStateHelper,
        buildFlowEndSyncState = buildFlowEndSyncStateHelper
    } = deps;
    if (!panel || !data || !nodeType) return false;

    if (nodeType === "signal_output") {
        const labelInput = panel.querySelector("#flowSignalLabel");
        const scopeSelect = panel.querySelector("#flowSignalScope");
        const nameInput = panel.querySelector("#flowSignalName");
        const actionSelect = panel.querySelector("#flowSignalAction");
        const nextState = buildFlowSignalOutputAppliedDataState({
            label: labelInput?.value || "",
            signalScope: normalizeSignalScope(scopeSelect?.value),
            signalName: nameInput?.value || "",
            action: actionSelect?.value || ""
        }, ensureSignalOutputData);
        Object.assign(data, nextState);
        if (nextState.shouldCommitInternalFlag) commitInternalFlagName(data.signalName);
        return true;
    }

    if (nodeType === "wait") {
        const labelInput = panel.querySelector("#flowWaitLabel");
        const durationInput = panel.querySelector("#flowWaitDurationSec");
        const nextState = buildFlowWaitSyncState({
            label: labelInput?.value || "",
            durationSec: durationInput?.value || 1
        });
        data.label = nextState.label;
        data.durationSec = nextState.durationSec;
        return true;
    }

    if (nodeType === "subprocess") {
        const nameInput = panel.querySelector("#flowSubprocessName");
        const durationInput = panel.querySelector("#flowSubprocessDurationSec");
        const subprocess = findSubprocessById(data.subprocessId);
        if (!subprocess) return true;
        const nextState = buildFlowSubprocessSyncState({
            subprocessName: nameInput?.value || "",
            durationSec: durationInput?.value || 1,
            isDuplicate: isSubprocessNameDuplicate((nameInput?.value || "").trim() || "サブプロセス", subprocess.id)
        });
        if (nextState.ok) {
            subprocess.name = nextState.subprocessName;
            data.label = nextState.label;
            data.subprocessName = nextState.subprocessName;
        }
        subprocess.durationSec = nextState.durationSec;
        data.durationSec = nextState.durationSec;
        return true;
    }

    if (nodeType === "error") {
        const codeInput = panel.querySelector("#flowErrorCode");
        const messageInput = panel.querySelector("#flowErrorMessage");
        const nextState = buildFlowErrorSyncState({
            errorCode: codeInput?.value || "",
            errorMessage: messageInput?.value || ""
        });
        data.errorCode = nextState.errorCode;
        data.errorMessage = nextState.errorMessage;
        return true;
    }

    if (nodeType === "button") {
        const labelInput = panel.querySelector("#flowButtonLabel");
        const signalInput = panel.querySelector("#flowButtonSignal");
        const modeSelect = panel.querySelector("#flowButtonMode");
        const trueLabelInput = panel.querySelector("#flowButtonTrueLabel");
        const falseLabelInput = panel.querySelector("#flowButtonFalseLabel");
        const nextState = buildFlowButtonSyncState({
            label: labelInput?.value || "",
            signalName: signalInput?.value || "",
            mode: normalizeButtonMode(modeSelect?.value || "momentary"),
            trueLabel: trueLabelInput?.value || "",
            falseLabel: falseLabelInput?.value || ""
        });
        data.label = nextState.label;
        data.signalName = nextState.signalName;
        data.mode = nextState.mode;
        data.trueLabel = nextState.trueLabel;
        data.falseLabel = nextState.falseLabel;
        return true;
    }

    if (nodeType === "end") {
        const labelInput = panel.querySelector("#flowEndLabel");
        const resultSelect = panel.querySelector("#flowEndResult");
        const nextState = buildFlowEndSyncState({
            label: labelInput?.value || "",
            result: resultSelect?.value || "normal"
        });
        data.label = nextState.label;
        data.result = nextState.result;
        return true;
    }

    return false;
}

export function syncConditionFlowNodeEditorState(deps = {}) {
    const {
        panel,
        data,
        nodeType = "",
        normalizeConditionEntry = (entry) => entry,
        normalizeConditionEntries = (entries) => entries,
        ensureConditionDraftSignal = () => ({}),
        ensureConditionNodeData = (nextData) => nextData,
        buildFlowConditionSavedState = buildFlowConditionSavedStateHelper
    } = deps;
    if (!panel || !data || nodeType !== "condition") return false;

    const textInput = panel.querySelector("#flowConditionText");
    const modeSelect = panel.querySelector("#flowConditionMode");
    const operatorSelect = panel.querySelector("#flowConditionOperator");
    const singleExpression = panel.querySelector("#flowConditionExpression");
    const structuredName = panel.querySelector("#flowStructuredConditionName");
    const kindSelect = panel.querySelector("#flowConditionKind");
    const valueSelect = panel.querySelector("#flowStructuredConditionValue");
    const actuatorSelect = panel.querySelector("#flowStructuredConditionActuatorId");
    const trueLabel = panel.querySelector("#flowConditionTrueLabel");
    const falseLabel = panel.querySelector("#flowConditionFalseLabel");
    const timeoutLabel = panel.querySelector("#flowConditionTimeoutLabel");
    const timeoutSec = panel.querySelector("#flowConditionTimeoutSec");
    const compositeRows = [...panel.querySelectorAll("[data-condition-field='label']")].map((labelInput) => {
        const index = Number(labelInput.getAttribute("data-condition-index"));
        const expressionInput = panel.querySelector(`[data-condition-field='expression'][data-condition-index='${index}']`);
        return {
            label: labelInput.value,
            expression: expressionInput?.value
        };
    });
    const nextState = buildFlowConditionSavedState(
        {
            conditionText: textInput?.value || "",
            conditionMode: modeSelect?.value || "single",
            operator: operatorSelect?.value || "AND",
            singleExpression: singleExpression ? singleExpression.value : null,
            structuredName: structuredName ? structuredName.value : null,
            structuredKind: kindSelect?.value || "signal",
            structuredValue: valueSelect?.value || "TRUE",
            structuredActuatorId: actuatorSelect?.value || "",
            compositeRows,
            trueLabel: trueLabel?.value || "",
            falseLabel: falseLabel?.value || "",
            timeoutLabel: timeoutLabel?.value || "",
            timeoutSec: timeoutSec?.value ?? null
        },
        {
            normalizeConditionEntry,
            normalizeConditionEntries,
            ensureConditionDraftSignal,
            ensureConditionNodeData
        }
    );
    Object.assign(data, nextState);
    return true;
}

export function syncActiveFlowNodeEditorState(deps = {}) {
    const {
        state,
        doc = typeof document !== "undefined" ? document : null,
        getFlowNodeById = () => null,
        getNodeData = (node) => node?.data,
        getNodeTypeFromData = () => "",
        syncConditionFlowNodeEditorState: syncConditionState = syncConditionFlowNodeEditorState,
        syncBasicFlowNodeEditorState: syncBasicState = syncBasicFlowNodeEditorState,
        refreshFlowNodeHtml = () => {},
        options = {}
    } = deps;
    const nodeId = state?.selectedFlowNodeId;
    if (!nodeId) return false;
    const panel = doc?.getElementById?.("flowNodeEditor") || null;
    const node = getFlowNodeById(nodeId);
    if (!panel || !node) return false;
    const data = getNodeData(node);
    const nodeType = getNodeTypeFromData(data);

    syncConditionState({
        ...deps,
        panel,
        data,
        nodeType
    });
    syncBasicState({
        ...deps,
        panel,
        data,
        nodeType
    });
    if (options.refresh !== false) refreshFlowNodeHtml(nodeId);
    return true;
}

export function refreshSubprocessReferenceNodes(deps = {}) {
    const {
        subprocess,
        state,
        getActiveFlowModuleName = () => "Home",
        getNodeData = (node) => node?.data,
        getNodeTypeFromData = () => "",
        refreshFlowNodeHtml = () => {}
    } = deps;
    if (!subprocess?.id || !state?.drawflow) return false;
    const modules = state.drawflow?.drawflow?.drawflow || {};
    const activeModuleName = getActiveFlowModuleName();
    let updated = false;
    for (const [moduleName, moduleData] of Object.entries(modules)) {
        for (const node of Object.values(moduleData?.data || {})) {
            const data = getNodeData(node);
            if (getNodeTypeFromData(data) !== "subprocess" || data.subprocessId !== subprocess.id) continue;
            data.label = subprocess.name;
            data.subprocessName = subprocess.name;
            data.durationSec = subprocess.durationSec;
            updated = true;
            if (moduleName === activeModuleName) {
                refreshFlowNodeHtml(String(node.id));
            }
        }
    }
    return updated;
}

export function renderFlowConnectionAssist(deps = {}) {
    const {
        state,
        doc = typeof document !== "undefined" ? document : null,
        getFlowNodeLabel = () => "",
        updateFlowConnectionSelectionState = () => {}
    } = deps;
    const hint = doc?.getElementById?.("flowConnectionHint") || null;
    const dragButton = doc?.getElementById?.("btnFlowConnectDrag") || null;
    const pickButton = doc?.getElementById?.("btnFlowConnectPick") || null;
    if (dragButton) {
        const active = state?.ui?.flowConnectionMode === "drag";
        dragButton.classList.toggle("is-active", active);
        dragButton.setAttribute("aria-pressed", String(active));
    }
    if (pickButton) {
        const active = state?.ui?.flowConnectionMode === "pick";
        pickButton.classList.toggle("is-active", active);
        pickButton.setAttribute("aria-pressed", String(active));
    }
    if (!hint) {
        updateFlowConnectionSelectionState();
        return false;
    }
    const pending = state?.pendingFlowConnection;
    if (pending) {
        hint.textContent = `${getFlowNodeLabel(pending.nodeId)} -> 次は入力端子をクリック`;
        hint.classList.add("is-pending");
        hint.classList.add("is-visible");
        updateFlowConnectionSelectionState();
        return true;
    }
    if (state?.ui?.flowConnectionHoverText) {
        hint.textContent = state.ui.flowConnectionHoverText;
        hint.classList.remove("is-pending");
        hint.classList.add("is-visible");
        updateFlowConnectionSelectionState();
        return true;
    }
    hint.textContent = "";
    hint.classList.remove("is-pending");
    hint.classList.remove("is-visible");
    updateFlowConnectionSelectionState();
    return true;
}

export function updateFlowConnectionSelectionState(deps = {}) {
    const {
        state,
        doc = typeof document !== "undefined" ? document : null
    } = deps;
    const container = doc?.getElementById?.("drawflow-container") || null;
    if (!container) return false;
    container.classList.toggle("flow-connect-mode", state?.ui?.flowConnectionMode === "pick");
    container.querySelectorAll(".drawflow-node.flow-connection-source").forEach((nodeEl) => {
        nodeEl.classList.remove("flow-connection-source");
    });
    container.querySelectorAll(".output.flow-connection-source-port").forEach((portEl) => {
        portEl.classList.remove("flow-connection-source-port");
    });
    const pending = state?.pendingFlowConnection;
    if (!pending || state?.ui?.flowConnectionMode !== "pick") return true;
    const nodeEl = doc.getElementById(`node-${pending.nodeId}`);
    if (!nodeEl) return true;
    nodeEl.classList.add("flow-connection-source");
    nodeEl.querySelector(`.${pending.outputName}`)?.classList.add("flow-connection-source-port");
    return true;
}

export function setFlowConnectionHoverText(deps = {}) {
    const {
        state,
        text = "",
        renderFlowConnectionAssist = () => {}
    } = deps;
    if (!state?.ui) return false;
    state.ui.flowConnectionHoverText = String(text || "");
    renderFlowConnectionAssist();
    return true;
}

export function updateFlowAxisSelect(deps = {}) {
    const {
        state,
        doc = typeof document !== "undefined" ? document : null,
        escapeHtml = (value) => String(value ?? "")
    } = deps;
    const select = doc?.getElementById?.("flowAxisSelect") || null;
    if (!select) return false;
    const axes = Array.isArray(state?.project?.axes) ? state.project.axes : [];
    const current = state?.selectedAxisId || select.value;
    select.innerHTML = axes.map((axis) => `<option value="${axis.id}">${escapeHtml(axis.name)}</option>`).join("");
    if (!axes.length) {
        select.innerHTML = `<option value="">軸なし</option>`;
        select.disabled = true;
        return true;
    }
    select.disabled = false;
    const targetValue = axes.some((axis) => axis.id === current) ? current : axes[0].id;
    select.value = targetValue;
    return true;
}

export function clearPendingFlowConnection(deps = {}) {
    const {
        state,
        renderFlowConnectionAssist = () => {}
    } = deps;
    if (!state) return false;
    state.pendingFlowConnection = null;
    renderFlowConnectionAssist();
    return true;
}

export function setFlowConnectionMode(deps = {}) {
    const {
        state,
        mode = "drag",
        renderFlowConnectionAssist = () => {}
    } = deps;
    if (!state?.ui) return false;
    state.ui.flowConnectionMode = mode === "pick" ? "pick" : "drag";
    if (state.ui.flowConnectionMode !== "pick") {
        state.pendingFlowConnection = null;
    }
    renderFlowConnectionAssist();
    return state.ui.flowConnectionMode;
}

export function beginFlowConnection(deps = {}) {
    const {
        state,
        nodeId,
        outputName,
        getActiveFlowModuleName = () => "Home",
        clearPendingFlowConnection: clearPending = clearPendingFlowConnection,
        renderFlowConnectionAssist = () => {}
    } = deps;
    if (!state || !nodeId || !outputName) return false;
    const pending = state.pendingFlowConnection;
    const moduleName = getActiveFlowModuleName();
    if (pending && pending.nodeId === nodeId && pending.outputName === outputName && pending.moduleName === moduleName) {
        clearPending({ state, renderFlowConnectionAssist });
        return true;
    }
    state.pendingFlowConnection = {
        nodeId,
        outputName,
        moduleName
    };
    renderFlowConnectionAssist();
    return true;
}

export function completeFlowConnection(deps = {}) {
    const {
        state,
        targetNodeId,
        inputName,
        getActiveFlowModuleName = () => "Home",
        clearPendingFlowConnection: clearPending = clearPendingFlowConnection,
        renderFlowConnectionAssist = () => {},
        addConnection = () => {},
        alertMessage = () => {},
        warn = () => {}
    } = deps;
    const pending = state?.pendingFlowConnection;
    if (!pending || !state?.drawflow || !targetNodeId || !inputName) return false;
    if (pending.moduleName !== getActiveFlowModuleName()) {
        clearPending({ state, renderFlowConnectionAssist });
        return true;
    }
    if (pending.nodeId === targetNodeId) {
        alertMessage("同じノードには接続できません。");
        return true;
    }
    try {
        addConnection(pending.nodeId, targetNodeId, pending.outputName, inputName);
        state.pendingFlowConnection = null;
        renderFlowConnectionAssist();
        return true;
    } catch (error) {
        warn("flow click connection failed", { pending, targetNodeId, inputName, error });
        alertMessage("接続できませんでした。既に接続済みか、組み合わせが無効です。");
        return true;
    }
}

export function renderFlowTabs(doc = document, deps = {}) {
    const {
        getActiveFlowModuleName = () => "Home",
        getSubprocessList = () => [],
        getSubprocessModuleName = (subprocessId) => `subprocess:${subprocessId}`,
        escapeHtml = (value) => String(value ?? ""),
        setActiveFlowModule = () => {},
        ensureSubprocessMeta = () => null,
        makeUniqueSubprocessName = (baseName) => baseName || "サブプロセス",
        ensureDrawflowModule = () => {},
        removeSubprocessMeta = () => false,
        getFlowExportSnapshot = () => ({}),
        getFlowModuleData = (snapshot, moduleName) => snapshot?.drawflow?.[moduleName]?.data || {},
        hasFlowModuleNodes = (options) => Object.keys(getFlowModuleData(options?.snapshot, options?.moduleName) || {}).length > 0,
        confirmMessage = (message) => typeof window === "undefined" ? true : window.confirm(message)
    } = deps;
    const root = doc.getElementById("flowTabs");
    if (!root) return null;
    const active = getActiveFlowModuleName();
    const parts = [
        `<button type="button" class="flow-tab${active === "Home" ? " is-active" : ""}" data-flow-module="Home">メイン</button>`
    ];
    for (const subprocess of getSubprocessList()) {
        const moduleName = subprocess.moduleName || getSubprocessModuleName(subprocess.id);
        const name = subprocess.name || "サブプロセス";
        parts.push(
            `<span class="flow-tab flow-tab-subprocess${active === moduleName ? " is-active" : ""}" data-flow-subprocess-tab="${escapeHtml(subprocess.id)}">` +
            `<button type="button" class="flow-tab-main" data-flow-module="${escapeHtml(moduleName)}">${escapeHtml(name)}</button>` +
            `<button type="button" class="flow-tab-close" data-flow-close-subprocess="${escapeHtml(subprocess.id)}" data-flow-module-name="${escapeHtml(moduleName)}" data-flow-subprocess-name="${escapeHtml(name)}" aria-label="${escapeHtml(name)}を閉じる">×</button>` +
            `</span>`
        );
    }
    parts.push(`<button type="button" class="flow-tab flow-tab-add" data-flow-add="1">＋</button>`);
    root.innerHTML = parts.join("");
    root.querySelectorAll("[data-flow-module]").forEach((button) => {
        button.addEventListener("click", () => setActiveFlowModule(button.dataset.flowModule || "Home"));
    });
    root.querySelectorAll("[data-flow-close-subprocess]").forEach((button) => {
        button.addEventListener("click", (event) => {
            event?.stopPropagation?.();
            const subprocessId = button.dataset.flowCloseSubprocess || "";
            const moduleName = button.dataset.flowModuleName || "";
            const subprocessName = button.dataset.flowSubprocessName || "サブプロセス";
            const snapshot = getFlowExportSnapshot();
            if (hasFlowModuleNodes({ moduleName, snapshot, getFlowModuleData })) {
                const confirmed = confirmMessage(`サブプロセス「${subprocessName}」には内部フローがあります。閉じると内部フローも削除されます。削除しますか？`);
                if (!confirmed) return;
            }
            removeSubprocessMeta(subprocessId);
        });
    });
    root.querySelector("[data-flow-add='1']")?.addEventListener("click", () => {
        const row = ensureSubprocessMeta({
            name: makeUniqueSubprocessName("サブプロセス"),
            durationSec: 1
        });
        if (!row) return;
        ensureDrawflowModule(row.moduleName);
        renderFlowTabs(doc, deps);
        setActiveFlowModule(row.moduleName);
    });
    return root;
}

export function renderBasicFlowNodeEditor(deps = {}) {
    const {
        panel,
        nodeId,
        nodeType = "",
        data,
        escapeHtml = (value) => String(value ?? ""),
        flowNodeEditorHeader = (title) => `<h3>${title}</h3>`,
        finishFlowNodeEditor = () => {},
        refreshFlowNodeHtml = () => {},
        ensureButtonNodeData = () => {},
        ensureSignalOutputData = () => {},
        normalizeButtonMode = (value) => value ?? "",
        syncInternalFlagsFromFlowSnapshot = () => {},
        getInternalFlagList = () => [],
        getConditionMode = () => "simple",
        getConditionKind = () => "signal",
        getConditionOperator = () => "all",
        ensureConditionNodeData = () => {},
        ensureConditionDraftSignal = () => {},
        normalizeConditionEntries = () => [],
        normalizeConditionEntry = (entry) => entry,
        findMotion = () => null,
        applyFlowMotionActuatorType = () => null,
        ensureFlowMotionActuatorBinding = () => ({ changed: false }),
        resolveFlowMotionActuatorType = () => "",
        findActuatorById = () => null,
        buildFlowActuatorTypeOptions = () => "",
        renderControlSpecActuators = () => {},
        recomputeAll = () => {},
        renderControlSpecView = () => {},
        scheduleAutoSave = () => {},
        renderErrors = () => {},
        renderTimingGrid = () => {},
        renderSpeedChart = () => {},
        updateAllFlowNodeLabels = () => {},
        isControlSpecViewActive = false,
        normalizeSignalScope = (value) => value ?? "",
        commitInternalFlagName = () => {},
        renderFlowTabs = () => {},
        renderFlowNodeEditor = () => {},
        findSubprocessById = () => null,
        ensureSubprocessMeta = () => null,
        getSubprocessList = () => [],
        isSubprocessNameDuplicate = () => false,
        refreshSubprocessReferenceNodes = () => {},
        ensureDrawflowModule = () => {},
        setActiveFlowModule = () => {},
        mountFlowErrorEditor = null,
        mountFlowButtonEditor = null,
        mountFlowEndEditor = null,
        mountFlowSignalOutputEditor = null,
        mountFlowWaitEditor = null,
        mountFlowSubprocessEditor = null,
        mountFlowConditionEditor = null,
        buildFlowActionSelectionState = (_actions = [], selectedActionId = "", _escapeHtml = (value) => String(value ?? "")) => ({ selectedActionId }),
        buildFlowMotionEditorState = ({ found, selectedActuatorType, actionState, selectedAction }) => ({
            found,
            selectedActuatorType,
            actionState,
            selectedAction,
            selectedActionId: actionState?.selectedActionId || ""
        }),
        buildFlowMotionAppliedDataState = (state) => state,
        mountFlowMotionEditor = null,
        actuators = []
    } = deps;
    if (!panel) return false;

    if (nodeType === "motion" && typeof mountFlowMotionEditor === "function") {
        const found = data?.motionId ? findMotion(data.motionId) : null;
        const canReuseMotionDraft = panel?.dataset?.flowEditorNodeId === String(nodeId || "");
        if (found?.motion && canReuseMotionDraft) {
            const nameInput = panel.querySelector("#flowMotionName");
            const actuatorSelect = panel.querySelector("#flowMotionActuatorId");
            const actionSelect = panel.querySelector("#flowMotionActionId");
            const actuator = applyFlowMotionActuatorType(data, found, actuatorSelect?.value || "");
            const nextState = buildFlowMotionAppliedDataState(
                {
                    motionName: nameInput?.value || "",
                    actionId: actionSelect?.value || "",
                    actions: Array.isArray(actuator?.actions) ? actuator.actions : []
                },
                escapeHtml
            );
            Object.assign(found.motion, { name: nextState.motionName });
            Object.assign(data, { actionId: nextState.actionId });
        }
        const autoBound = found ? ensureFlowMotionActuatorBinding(data, found) : { changed: false };
        const selectedActuatorType = resolveFlowMotionActuatorType(data, found);
        const selectedActuator = findActuatorById(data?.actuatorId || "");
        const actionState = buildFlowActionSelectionState(
            Array.isArray(selectedActuator?.actions) ? selectedActuator.actions : [],
            data?.actionId || "",
            escapeHtml
        );
        const selectedAction = Array.isArray(selectedActuator?.actions)
            ? selectedActuator.actions.find((action) => action?.id === actionState.selectedActionId) || null
            : null;
        const resolveMotionEditorState = typeof buildFlowMotionEditorState === "function"
            ? buildFlowMotionEditorState
            : buildFlowMotionEditorStateFallback;
        const editorState = resolveMotionEditorState({
            found,
            selectedActuatorType,
            actionState,
            selectedAction,
            selectedActuator
        });
        if ((data?.actionId || "") !== editorState.selectedActionId) {
            data.actionId = editorState.selectedActionId;
        }
        if (autoBound.changed) {
            refreshFlowNodeHtml(nodeId);
            if (isControlSpecViewActive) renderControlSpecActuators();
            recomputeAll();
            renderControlSpecView({ skipValidationRefresh: true });
            scheduleAutoSave();
        }
        mountFlowMotionEditor(panel, {
            editorState,
            actuatorOptionsHtml: buildFlowActuatorTypeOptions(editorState.selectedActuatorType),
            headerHtml: flowNodeEditorHeader("動作ノード編集"),
            escapeHtml,
            found,
            data,
            applyFlowMotionActuatorType,
            refreshFlowNodeHtml: () => refreshFlowNodeHtml(nodeId),
            renderControlSpecActuators,
            recomputeAll,
            renderErrors,
            renderTimingGrid,
            renderSpeedChart,
            scheduleAutoSave,
            updateAllFlowNodeLabels,
            isControlSpecViewActive
        });
        finishFlowNodeEditor(panel, nodeId);
        return true;
    }
    if (nodeType === "error" && typeof mountFlowErrorEditor === "function") {
        mountFlowErrorEditor(panel, {
            data,
            escapeHtml,
            headerHtml: flowNodeEditorHeader("エラーノード編集"),
            refreshFlowNodeHtml: () => refreshFlowNodeHtml(nodeId)
        });
        finishFlowNodeEditor(panel, nodeId);
        return true;
    }
    if (nodeType === "button" && typeof mountFlowButtonEditor === "function") {
        ensureButtonNodeData(data);
        mountFlowButtonEditor(panel, {
            data,
            mode: normalizeButtonMode(data?.mode),
            escapeHtml,
            headerHtml: flowNodeEditorHeader("操作入力ノード編集"),
            normalizeButtonMode,
            refreshFlowNodeHtml: () => refreshFlowNodeHtml(nodeId)
        });
        finishFlowNodeEditor(panel, nodeId);
        return true;
    }
    if (nodeType === "signal_output" && typeof mountFlowSignalOutputEditor === "function") {
        ensureSignalOutputData(data);
        syncInternalFlagsFromFlowSnapshot();
        mountFlowSignalOutputEditor(panel, {
            data,
            getInternalFlagList,
            normalizeSignalScope,
            commitInternalFlagName,
            renderFlowNodeEditor,
            refreshFlowNodeHtml: () => refreshFlowNodeHtml(nodeId),
            escapeHtml,
            headerHtml: flowNodeEditorHeader("信号操作ノード編集")
        });
        finishFlowNodeEditor(panel, nodeId);
        return true;
    }
    if (nodeType === "wait" && typeof mountFlowWaitEditor === "function") {
        mountFlowWaitEditor(panel, {
            data,
            escapeHtml,
            headerHtml: flowNodeEditorHeader("待機ノード編集"),
            refreshFlowNodeHtml: () => refreshFlowNodeHtml(nodeId)
        });
        finishFlowNodeEditor(panel, nodeId);
        return true;
    }
    if (nodeType === "condition" && typeof mountFlowConditionEditor === "function") {
        ensureConditionNodeData(data);
        syncInternalFlagsFromFlowSnapshot();
        const conditionMode = getConditionMode(data);
        mountFlowConditionEditor(panel, {
            data,
            conditionMode,
            conditionKind: getConditionKind(data),
            conditionRows: normalizeConditionEntries(data?.conditions, data?.conditionText || "条件", conditionMode),
            operator: getConditionOperator(data),
            internalFlags: getInternalFlagList(),
            actuators,
            headerHtml: flowNodeEditorHeader("条件ノード編集"),
            escapeHtml,
            getConditionMode,
            getConditionKind,
            ensureConditionDraftSignal,
            normalizeConditionEntries,
            normalizeConditionEntry,
            renderFlowNodeEditor,
            refreshFlowNodeHtml: () => refreshFlowNodeHtml(nodeId)
        });
        finishFlowNodeEditor(panel, nodeId);
        return true;
    }
    if (nodeType === "subprocess" && typeof mountFlowSubprocessEditor === "function") {
        const subprocess = findSubprocessById(data?.subprocessId) || ensureSubprocessMeta({
            id: data?.subprocessId,
            name: data?.subprocessName || data?.label || "サブプロセス",
            durationSec: data?.durationSec
        });
        mountFlowSubprocessEditor(panel, {
            data,
            subprocess,
            subprocesses: getSubprocessList(),
            escapeHtml,
            headerHtml: flowNodeEditorHeader("サブプロセス編集"),
            findSubprocessById,
            isSubprocessNameDuplicate,
            refreshSubprocessReferenceNodes,
            renderFlowTabs,
            renderFlowNodeEditor,
            refreshFlowNodeHtml: () => refreshFlowNodeHtml(nodeId),
            ensureDrawflowModule,
            setActiveFlowModule
        });
        finishFlowNodeEditor(panel, nodeId);
        return true;
    }
    if (nodeType === "end" && typeof mountFlowEndEditor === "function") {
        mountFlowEndEditor(panel, {
            data,
            escapeHtml,
            headerHtml: flowNodeEditorHeader("終了ノード編集"),
            refreshFlowNodeHtml: () => refreshFlowNodeHtml(nodeId)
        });
        finishFlowNodeEditor(panel, nodeId);
        return true;
    }
    if (nodeType) {
        panel.innerHTML = `${flowNodeEditorHeader("選択ノード編集")}<div class="help">このノード種別はここでは編集できません。</div>`;
        finishFlowNodeEditor(panel, nodeId);
        return true;
    }
    return false;
}
